/**
 * FacilityPro Master Admin — control centre shown only to the eln-org root login.
 * Ported from the facilitypro-master-admin design and backed by the Org/MasterAdmin/* APIs
 * (customer tenants, support inbox, platform Stripe invoices, system health, organization actions and their
 * audit log) and the public FacilityPro pricing file.
 */
import { createContext, Fragment, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Divider, IconButton, InputAdornment, LinearProgress, List,
  FormControlLabel, ListItemButton, ListItemIcon, ListItemText, MenuItem, Paper, Radio, RadioGroup, Snackbar, Stack, Switch, Tab, Tabs, TextField,
  Tooltip as MuiTooltip, Typography,
} from '@mui/material'
import type { ChipProps } from '@mui/material'
import {
  AnalyticsRounded, ApartmentRounded, BlockRounded, BusinessRounded, CheckCircleOutlineRounded, ContentCopyRounded,
  AddRounded, ArrowDownwardRounded, ArrowUpwardRounded, CreditCardRounded, DashboardRounded, DeleteForeverRounded, EditRounded, ForwardToInboxRounded, HistoryRounded, PersonAddRounded, PersonRemoveRounded, PlayCircleOutlineRounded, VpnKeyRounded, GroupsRounded, HealthAndSafetyRounded, LogoutRounded, MonitorHeartRounded, OpenInNewRounded,
  ReceiptLongRounded, MoreTimeRounded, ErrorOutlineRounded, ApiRounded, AccountTreeRounded,
  RefreshRounded, SearchRounded, SendRounded, SettingsRounded, SupportAgentRounded, SwapHorizRounded,
  VisibilityOffRounded, VisibilityRounded,
} from '@mui/icons-material'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { authApi } from '@/services/auth'
import { getErrorMessage } from '@/lib/api'
import type { HelpSupportRequest, HelpSupportStatus } from '@/services/help-support'
import { HelpAttachmentLinks, HelpAttachmentPicker } from '@/components/help-support/attachments'
import {
  masterAdminApi,
  masterAdminQueryKeys,
  ORG_STATUS_CODE,
  type MasterAdminAuditEntry,
  type MasterAdminPermission,
  type MasterAdminRole,
  type MasterAdminStaffMember,
  type PricingCurrencyRow,
  type PricingDocument,
  type PricingBillingRole,
  type PricingPackageRow,
  type PricingPrice,
  type StaffInviteResult,
  type BillingOrganization,
  type IntegrationCostReport,
  type CurrencyTotal,
  type MasterAdminOrganization,
  type MasterAdminParentOrg,
  type OrgStatusAction,
  type PlatformSettings,
  type SupportAccessCredentials,
  type MasterAdminOverview,
  type MasterAdminSupportList,
  type MasterAdminPlan,
  type HealthCheck,
  type HealthCheckStatus,
  type SystemHealthStatus,
} from '@/services/master-admin'
import { useAuthStore } from '@/stores/auth-store'
import './master-admin.css'

type PageName =
  | 'Dashboard' | 'Organizations' | 'Parent Orgs' | 'Organization Detail' | 'Pricing Plans' | 'Billing' | 'Integration Costs'
  | 'Invoices' | 'Support' | 'Usage & Health' | 'System Health' | 'Reports' | 'Audit Log' | 'Team' | 'Platform Settings'

const menu: [PageName, ReactNode][] = [
  ['Dashboard', <DashboardRounded />],
  ['Organizations', <ApartmentRounded />],
  ['Parent Orgs', <AccountTreeRounded />],
  ['Pricing Plans', <CreditCardRounded />],
  ['Billing', <CreditCardRounded />],
  ['Invoices', <ReceiptLongRounded />],
  ['Integration Costs', <ApiRounded />],
  ['Support', <SupportAgentRounded />],
  ['Usage & Health', <HealthAndSafetyRounded />],
  ['System Health', <MonitorHeartRounded />],
  ['Reports', <AnalyticsRounded />],
  ['Audit Log', <HistoryRounded />],
  ['Team', <GroupsRounded />],
  ['Platform Settings', <SettingsRounded />],
]

/** Pages only some master admin roles can open; every role can view the rest. */
const PAGE_PERMISSION: Partial<Record<PageName, MasterAdminPermission>> = { Team: 'staff_manage' }

/** The signed-in user's master admin permissions (from Org/MasterAdmin/Me/). Empty until loaded. */
const AccessContext = createContext<MasterAdminPermission[]>([])
const useCan = () => {
  const permissions = useContext(AccessContext)
  return (permission: MasterAdminPermission) => permissions.includes(permission)
}

const PLAN_META: Record<MasterAdminPlan, { label: string; color: ChipProps['color'] }> = {
  trial: { label: 'Trial', color: 'info' },
  active: { label: 'Paid', color: 'success' },
  exempt: { label: 'Not billed yet', color: 'default' },
  locked: { label: 'Locked', color: 'error' },
  not_billed: { label: 'Not billed', color: 'warning' },
}

const SUPPORT_STATUS: Record<HelpSupportStatus, { label: string; color: ChipProps['color'] }> = {
  open: { label: 'Open', color: 'warning' },
  in_progress: { label: 'In progress', color: 'info' },
  resolved: { label: 'Resolved', color: 'success' },
  closed: { label: 'Closed', color: 'default' },
}

const ORG_STATUS_COLOR: Record<string, ChipProps['color']> = {
  Approved: 'success', Pending: 'warning', Suspended: 'error', Rejected: 'error', Terminated: 'default',
}

type OrgActionKind = OrgStatusAction | 'support_access'

const ORG_ACTIONS: Record<OrgActionKind, {
  label: string
  title: string
  icon: ReactNode
  color: 'primary' | 'warning' | 'error' | 'success'
  explain: string
}> = {
  suspend: {
    label: 'Suspend',
    title: 'Suspend organization',
    icon: <BlockRounded />,
    color: 'warning',
    explain: 'Every user in this organization and its branches is signed out on their next request and can\'t sign in until you reactivate it. No data is deleted.',
  },
  reactivate: {
    label: 'Reactivate',
    title: 'Reactivate organization',
    icon: <PlayCircleOutlineRounded />,
    color: 'success',
    explain: 'Users can sign in and work again straight away.',
  },
  terminate: {
    label: 'Terminate',
    title: 'Terminate organization',
    icon: <DeleteForeverRounded />,
    color: 'error',
    explain: 'Closes the account: nobody in this organization can sign in and it drops out of the customer totals. Data is kept, and you can still reactivate it later.',
  },
  support_access: {
    label: 'Support access',
    title: 'Generate support access',
    icon: <VpnKeyRounded />,
    color: 'primary',
    explain: 'Resets the hidden support login inside this organization and shows its password once, so you can sign in and see what the customer sees. Any earlier support password stops working.',
  },
}

const ORG_ACTION_PERMISSION: Record<OrgActionKind, MasterAdminPermission> = {
  suspend: 'org_status',
  reactivate: 'org_status',
  terminate: 'org_terminate',
  support_access: 'support_access',
}

/** Which actions make sense from each ServiceRegistrationChoices status. */
function availableOrgActions(statusCode: number): OrgActionKind[] {
  switch (statusCode) {
    case ORG_STATUS_CODE.pending:
    case ORG_STATUS_CODE.approved:
      return ['support_access', 'suspend', 'terminate']
    case ORG_STATUS_CODE.suspended:
      return ['reactivate', 'terminate']
    case ORG_STATUS_CODE.terminated:
      return ['reactivate']
    default:
      return []
  }
}

const formatDate = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === '') return '—'
  const date = typeof value === 'number' ? new Date(value * 1000) : new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}
const formatTime = (value: string | null | undefined) => {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? ''
    : `${date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} · ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
}
const formatMoney = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(amount)
  } catch {
    return `${currency} ${amount.toFixed(2)}`
  }
}
const matches = (row: object, query: string) => JSON.stringify(row).toLowerCase().includes(query.toLowerCase())
const initials = (value: string) => value.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || 'A'
const shortVertical = (vertical: string) => vertical.replace(/\s*Parent Organization$/i, '') || '—'
/** Where a company sits: its product parent org, or directly under the Root Org. */
const parentLabel = (o: MasterAdminOrganization) =>
  o.parent_org?.is_root ? 'Root Org (direct)' : shortVertical(o.parent_org?.name ?? o.vertical)

function PlanChip({ org }: { org: MasterAdminOrganization }) {
  const meta = PLAN_META[org.billing.plan] ?? PLAN_META.trial
  return <Chip size="small" color={meta.color} label={meta.label} />
}

const SYSTEM_STATUS: Record<SystemHealthStatus, { label: string; banner: string; color: ChipProps['color'] }> = {
  ok: { label: 'All systems operational', banner: 'All systems operational', color: 'success' },
  degraded: { label: 'Degraded', banner: 'Some services need attention', color: 'warning' },
  down: { label: 'Down', banner: 'Platform is down', color: 'error' },
}

const CHECK_STATUS: Record<HealthCheckStatus, { label: string; color: ChipProps['color'] }> = {
  ok: { label: 'Healthy', color: 'success' },
  warn: { label: 'Warning', color: 'warning' },
  fail: { label: 'Failing', color: 'error' },
  skipped: { label: 'Not checked', color: 'default' },
}

const CHECK_LABELS: Record<string, { title: string; description: string }> = {
  database: { title: 'Database', description: 'PostgreSQL connection and query round-trip' },
  cache: { title: 'Cache', description: 'Redis read/write used for sessions and caching' },
  celery_broker: { title: 'Task queue broker', description: 'Redis queue that background jobs are sent to' },
  celery_heartbeat: { title: 'Background workers', description: 'Celery beat schedules a heartbeat every minute and a worker runs it' },
  disk: { title: 'Disk space', description: 'Free space on the application and uploads volumes' },
  memory: { title: 'Memory (RAM)', description: 'Server memory in use, excluding reclaimable cache' },
  cpu: { title: 'CPU load', description: '5-minute load average per CPU core; 1.0 means every core is busy' },
  migrations: { title: 'Database migrations', description: 'Unapplied migrations usually mean a deploy skipped migrate' },
}

function Header({ query, setQuery, onRefresh, refreshing, health, onHealthClick }: {
  query: string
  setQuery: (q: string) => void
  onRefresh: () => void
  refreshing: boolean
  health: SystemHealthStatus | undefined
  onHealthClick: () => void
}) {
  return (
    <Box className="header">
      <TextField
        size="small"
        placeholder="Search organizations, invoices, requests..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ width: 390 }}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchRounded /></InputAdornment> } }}
      />
      <Box flex={1} />
      {health && (
        <Chip
          icon={<MonitorHeartRounded />}
          color={SYSTEM_STATUS[health].color}
          variant={health === 'ok' ? 'outlined' : 'filled'}
          label={SYSTEM_STATUS[health].label}
          onClick={onHealthClick}
        />
      )}
      <MuiTooltip title="Refresh data">
        <span>
          <IconButton onClick={onRefresh} disabled={refreshing}>
            {refreshing ? <CircularProgress size={20} /> : <RefreshRounded />}
          </IconButton>
        </span>
      </MuiTooltip>
    </Box>
  )
}

function Sidebar({ page, setPage, userName, roleName, onClassic, onLogout }: {
  page: PageName
  setPage: (p: PageName) => void
  userName: string
  roleName: string
  /** Only Owners may leave for the classic root-org portal. */
  onClassic?: () => void
  onLogout: () => void
}) {
  const can = useCan()
  return (
    <Box className="sidebar">
      <Stack direction="row" spacing={1.2} alignItems="center" px={1}>
        <Box className="logo">F</Box>
        <Box>
          <Typography fontWeight={800}>FacilityPro</Typography>
          <Typography className="sideMuted">SaaS Control Centre</Typography>
        </Box>
      </Stack>
      <Typography className="section">MANAGEMENT</Typography>
      <List>
        {menu.filter(([name]) => !PAGE_PERMISSION[name] || can(PAGE_PERMISSION[name])).map(([name, icon]) => (
          <ListItemButton key={name} selected={page === name} className="nav" onClick={() => setPage(name)}>
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText primary={name} />
          </ListItemButton>
        ))}
      </List>
      <Stack className="profile" direction="row" spacing={1} alignItems="center">
        <Avatar>{initials(userName)}</Avatar>
        <Box flex={1} minWidth={0}>
          <Typography fontSize={13} fontWeight={700} noWrap>{userName}</Typography>
          <Typography className="sideMuted" noWrap>{roleName}</Typography>
        </Box>
        {onClassic && (
          <MuiTooltip title="Classic portal">
            <IconButton size="small" onClick={onClassic}><SwapHorizRounded fontSize="small" /></IconButton>
          </MuiTooltip>
        )}
        <MuiTooltip title="Log out">
          <IconButton size="small" onClick={onLogout}><LogoutRounded fontSize="small" /></IconButton>
        </MuiTooltip>
      </Stack>
    </Box>
  )
}

function Metric({ title, value, icon, sub }: { title: string; value: ReactNode; icon: ReactNode; sub: string }) {
  return (
    <Paper className="card">
      <Stack direction="row" justifyContent="space-between">
        <Box>
          <Typography className="eyebrow">{title}</Typography>
          <Typography variant="h5" fontWeight={800}>{value}</Typography>
        </Box>
        <Box className="metricIcon">{icon}</Box>
      </Stack>
      <Typography className="muted" mt={2}>{sub}</Typography>
    </Paper>
  )
}

function PageTitle({ title, sub, action }: { title: string; sub: string; action?: ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
      <Box>
        <Typography variant="h5" fontWeight={800}>{title}</Typography>
        <Typography className="muted">{sub}</Typography>
      </Box>
      {action}
    </Stack>
  )
}

function Info({ k, v }: { k: string; v: ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" className="info">
      <Typography className="muted">{k}</Typography>
      <Typography fontWeight={700}>{v}</Typography>
    </Stack>
  )
}

function Empty({ children }: { children: ReactNode }) {
  return <Box className="empty"><Typography className="muted">{children}</Typography></Box>
}

function QueryGate({ isLoading, error, children }: { isLoading: boolean; error: unknown; children: ReactNode }) {
  if (isLoading) {
    return <Box className="empty"><CircularProgress size={28} /></Box>
  }
  if (error) {
    return <Alert severity="error">{getErrorMessage(error, 'Unable to load data from the server.')}</Alert>
  }
  return <>{children}</>
}

function OrgTable({ orgs, openOrg, compact }: { orgs: MasterAdminOrganization[]; openOrg: (o: MasterAdminOrganization) => void; compact?: boolean }) {
  if (orgs.length === 0) {
    return <Empty>No customer organizations found.</Empty>
  }
  if (compact) {
    return (
      <>
        <Box className="table orgTable head"><span>ORGANIZATION</span><span>PARENT ORG</span><span>USERS</span><span>JOINED</span><span>PLAN</span><span /></Box>
        {orgs.map((o) => (
          <Box className="table orgTable row" key={o.id}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar variant="rounded">{initials(o.name)}</Avatar>
              <Box>
                <Typography fontWeight={700}>{o.name}</Typography>
                <Typography className="tiny">{o.country || '—'}</Typography>
              </Box>
            </Stack>
            <span>{parentLabel(o)}</span>
            <span>{o.billing.user_count}</span>
            <span>{formatDate(o.created_on)}</span>
            <PlanChip org={o} />
            <IconButton size="small" onClick={() => openOrg(o)}><VisibilityRounded fontSize="small" /></IconButton>
          </Box>
        ))}
      </>
    )
  }
  return (
    <>
      <Box className="table orgFull head"><span>ORGANIZATION</span><span>OWNER</span><span>PLAN</span><span>USERS</span><span>STATUS</span><span>JOINED</span><span>ACTIONS</span></Box>
      {orgs.map((o) => (
        <Box className="table orgFull row" key={o.id}>
          <Box>
            <Typography fontWeight={700}>{o.name}</Typography>
            <Typography className="tiny">{parentLabel(o)} · {o.country || '—'}</Typography>
          </Box>
          <Box>
            <span>{o.owner_name || '—'}</span>
            <Typography className="tiny">{o.owner_email}</Typography>
          </Box>
          <PlanChip org={o} />
          <span>{o.billing.user_count}</span>
          <Chip size="small" color={ORG_STATUS_COLOR[o.status] ?? 'default'} label={o.status} />
          <span>{formatDate(o.created_on)}</span>
          <IconButton size="small" onClick={() => openOrg(o)}><VisibilityRounded fontSize="small" /></IconButton>
        </Box>
      ))}
    </>
  )
}

function Dashboard({ overview, setPage, openOrg }: {
  overview: MasterAdminOverview
  setPage: (p: PageName) => void
  openOrg: (o: MasterAdminOrganization) => void
}) {
  const { totals, organizations } = overview
  const total = Math.max(totals.organizations, 1)
  const planRows: [string, number][] = [
    ['On trial', totals.plans.trial ?? 0],
    ['Paid subscription', totals.plans.active ?? 0],
    ['Not billed yet (card-exempt country)', totals.plans.exempt ?? 0],
    ['Locked', totals.plans.locked ?? 0],
    ...((totals.plans.not_billed ?? 0) > 0 ? [['Not billed (created by a parent org)', totals.plans.not_billed ?? 0] as [string, number]] : []),
  ]
  return (
    <>
      <PageTitle title="SaaS Overview" sub="Monitor customers, subscriptions, support and platform health." />
      <Box className="metrics">
        <Metric title="TOTAL ORGANIZATIONS" value={totals.organizations} icon={<BusinessRounded />} sub={`${totals.branches} branches across all tenants`} />
        <Metric title="PAID SUBSCRIPTIONS" value={totals.plans.active ?? 0} icon={<CreditCardRounded />} sub={`${totals.plans.trial ?? 0} organizations on trial`} />
        <Metric title="ACTIVE USERS" value={totals.users.toLocaleString()} icon={<GroupsRounded />} sub="Staff seats across all organizations" />
        <Metric title="OPEN SUPPORT REQUESTS" value={totals.open_support_requests} icon={<SupportAgentRounded />} sub="Open or in progress" />
      </Box>
      <Box className="two">
        <Paper className="card chart">
          <Typography fontWeight={800}>New organizations</Typography>
          <Typography className="muted">Customer signups per month</Typography>
          <Typography variant="h5" fontWeight={800} mt={2}>
            {overview.signups_by_month.reduce((a, m) => a + m.count, 0)}
            <Typography component="span" className="muted"> in the last {overview.signups_by_month.length} months</Typography>
          </Typography>
          <Box height={220}>
            <ResponsiveContainer>
              <AreaChart data={overview.signups_by_month}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Area type="monotone" dataKey="count" name="Signups" stroke="#3157d5" fill="#e9eeff" strokeWidth={3} />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
        <Paper className="card">
          <Typography fontWeight={800}>Subscription mix</Typography>
          <Typography className="muted">Where customer organizations are in their lifecycle</Typography>
          {planRows.map(([label, count]) => (
            <Box mt={3} key={label}>
              <Stack direction="row" justifyContent="space-between">
                <Typography>{label}</Typography>
                <Typography fontWeight={700}>{count}</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={(count / total) * 100} sx={{ mt: 1, height: 8, borderRadius: 5 }} />
            </Box>
          ))}
        </Paper>
      </Box>
      <Paper className="card orgCard">
        <Stack direction="row" justifyContent="space-between">
          <Box>
            <Typography fontWeight={800}>Organizations</Typography>
            <Typography className="muted">Most recent FacilityPro signups</Typography>
          </Box>
          <Button onClick={() => setPage('Organizations')}>View all</Button>
        </Stack>
        <Box className="tableScroll"><OrgTable orgs={organizations.slice(0, 5)} openOrg={openOrg} compact /></Box>
      </Paper>
    </>
  )
}

function Organizations({ overview, query, openOrg, parent, setParent }: {
  overview: MasterAdminOverview
  query: string
  openOrg: (o: MasterAdminOrganization) => void
  /** Parent org filter ('All' or a parent org label), kept by the page so Parent Orgs can set it. */
  parent: string
  setParent: (value: string) => void
}) {
  const [showTerminated, setShowTerminated] = useState(false)
  const terminatedCount = overview.organizations.filter((o) => o.status_code === ORG_STATUS_CODE.terminated).length
  const parents = ['All', ...Array.from(new Set(overview.organizations.map(parentLabel)))]
  const filtered = overview.organizations.filter((o) => matches(o, query)
    && (parent === 'All' || parentLabel(o) === parent)
    && (showTerminated || o.status_code !== ORG_STATUS_CODE.terminated))
  return (
    <>
      <PageTitle
        title="Organizations"
        sub="Every FacilityPro customer organization: self-service signups, and companies a parent org created by hand (Not billed)."
        action={(
          <Stack direction="row" spacing={1.5} alignItems="center">
            {terminatedCount > 0 && (
              <Button size="small" variant={showTerminated ? 'contained' : 'outlined'} color="inherit" onClick={() => setShowTerminated((v) => !v)}>
                {showTerminated ? 'Hide' : 'Show'} terminated ({terminatedCount})
              </Button>
            )}
            <TextField select size="small" label="Parent org" value={parents.includes(parent) ? parent : 'All'} onChange={(e) => setParent(e.target.value)} sx={{ minWidth: 200 }}>
              {parents.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
            </TextField>
          </Stack>
        )}
      />
      <Paper className="card tableCard"><OrgTable orgs={filtered} openOrg={openOrg} /></Paper>
    </>
  )
}

const PARENT_PLAN_COLUMNS: MasterAdminPlan[] = ['trial', 'active', 'exempt', 'locked', 'not_billed']

function ParentOrgs({ overview, query, openParent, notify }: {
  overview: MasterAdminOverview
  query: string
  /** Show this parent org's companies on the Organizations page. */
  openParent: (label: string) => void
  notify: (message: string) => void
}) {
  const canChangeStatus = useCan()('org_status')
  const [acting, setActing] = useState<{ parent: MasterAdminParentOrg; action: 'suspend' | 'reactivate' } | null>(null)
  const rows = (overview.parent_orgs ?? []).filter((p) => matches(p, query))
  const label = (p: MasterAdminParentOrg) => (p.is_root ? 'Root Org (direct)' : shortVertical(p.name))
  const isBlocked = (p: MasterAdminParentOrg) => p.status_code === PARENT_SUSPENDED || p.status_code === PARENT_TERMINATED
  return (
    <>
      <PageTitle
        title="Parent Orgs"
        sub="The product parent orgs under the Root Org and the companies in each. Companies a parent org created by hand show as Not billed."
      />
      <Paper className="card tableCard">
        <Box className="tableScroll">
          <Box className="table parentTable head">
            <span>PARENT ORG</span><span>COMPANIES</span><span>STAFF USERS</span>
            {PARENT_PLAN_COLUMNS.map((plan) => <span key={plan}>{PLAN_META[plan].label.toUpperCase()}</span>)}
            <span>STATUS</span><span />
          </Box>
          {rows.length === 0 && <Empty>No parent orgs found.</Empty>}
          {rows.map((p) => (
            <Box className="table parentTable row" key={p.id}>
              <Box>
                <Typography fontWeight={700}>{label(p)}</Typography>
                <Typography className="tiny">{p.is_root ? 'Companies with no product parent org' : p.identifier}</Typography>
              </Box>
              <span>
                {p.organizations}
                {p.terminated > 0 && <Typography component="span" className="tiny"> (+{p.terminated} terminated)</Typography>}
              </span>
              <span>{p.users}</span>
              {PARENT_PLAN_COLUMNS.map((plan) => <span key={plan}>{p.plans[plan] ?? 0}</span>)}
              <Chip size="small" color={ORG_STATUS_COLOR[p.status] ?? 'default'} label={p.status} />
              <Stack direction="row" spacing={0.5}>
                <MuiTooltip title="Show its companies">
                  <span>
                    <IconButton size="small" disabled={p.organizations + p.terminated === 0} onClick={() => openParent(label(p))}>
                      <VisibilityRounded fontSize="small" />
                    </IconButton>
                  </span>
                </MuiTooltip>
                {!p.is_root && canChangeStatus && (
                  isBlocked(p) ? (
                    <MuiTooltip title="Reactivate this parent org and its companies">
                      <IconButton size="small" color="success" onClick={() => setActing({ parent: p, action: 'reactivate' })}>
                        <PlayCircleOutlineRounded fontSize="small" />
                      </IconButton>
                    </MuiTooltip>
                  ) : (
                    <MuiTooltip title="Suspend this parent org and every company under it">
                      <IconButton size="small" color="warning" onClick={() => setActing({ parent: p, action: 'suspend' })}>
                        <BlockRounded fontSize="small" />
                      </IconButton>
                    </MuiTooltip>
                  )
                )}
              </Stack>
            </Box>
          ))}
        </Box>
      </Paper>
      {acting && (
        <ParentOrgActionDialog
          parent={acting.parent}
          action={acting.action}
          onClose={() => setActing(null)}
          onDone={(message) => { setActing(null); notify(message) }}
        />
      )}
    </>
  )
}

// ServiceRegistrationChoices: Suspended and Resigned (terminated) block sign-in.
const PARENT_SUSPENDED = 3
const PARENT_TERMINATED = 4

function ParentOrgActionDialog({ parent, action, onClose, onDone }: {
  parent: MasterAdminParentOrg
  action: 'suspend' | 'reactivate'
  onClose: () => void
  onDone: (message: string) => void
}) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [confirmName, setConfirmName] = useState('')
  const suspending = action === 'suspend'
  const mutation = useMutation({
    mutationFn: () => masterAdminApi.changeParentOrgStatus(parent.id, action, {
      reason: reason.trim(),
      ...(suspending ? { confirm_name: confirmName.trim() } : {}),
    }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['master-admin'] })
      onDone(`${parent.name}: ${suspending ? 'suspended' : 'reactivated'}.`)
    },
  })
  const canSubmit = reason.trim().length >= 5
    && (!suspending || confirmName.trim().toLowerCase() === parent.name.trim().toLowerCase())

  return (
    <Dialog open onClose={mutation.isPending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{suspending ? 'Suspend parent org' : 'Reactivate parent org'}: {parent.name}</DialogTitle>
      <DialogContent>
        {suspending ? (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Every company under it ({parent.organizations} {parent.organizations === 1 ? 'company' : 'companies'}, {parent.users} staff users) is
            blocked too: nobody can sign in and signed-in users are stopped on their next action. Each company keeps its own
            status, so reactivating the parent brings them all back.
          </Alert>
        ) : (
          <DialogContentText>
            The parent org and the companies under it can sign in again. A company that was suspended on its own stays suspended.
          </DialogContentText>
        )}
        <TextField
          label="Reason (saved in the audit log)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          margin="normal"
          autoFocus
          helperText="At least 5 characters."
        />
        {suspending && (
          <TextField
            label={`Type "${parent.name}" to confirm`}
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            fullWidth
            margin="dense"
          />
        )}
        {mutation.error && <Alert severity="error" sx={{ mt: 1 }}>{getErrorMessage(mutation.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
        <Button
          variant="contained"
          color={suspending ? 'warning' : 'success'}
          startIcon={mutation.isPending ? <CircularProgress size={16} /> : suspending ? <BlockRounded /> : <PlayCircleOutlineRounded />}
          disabled={!canSubmit || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {suspending ? 'Suspend' : 'Reactivate'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function OrgActionDialog({ org, action, onClose, onDone }: {
  org: MasterAdminOrganization
  action: OrgActionKind | null
  onClose: () => void
  onDone: (message: string, credentials?: SupportAccessCredentials) => void
}) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [confirmName, setConfirmName] = useState('')
  const [roleId, setRoleId] = useState('')
  useEffect(() => { setReason(''); setConfirmName(''); setRoleId('') }, [action])

  const rolesQuery = useQuery({
    queryKey: masterAdminQueryKeys.supportRoles(org.id),
    queryFn: () => masterAdminApi.supportRoles(org.id),
    enabled: action === 'support_access',
  })
  // Default to the role that sees what the customer's own admin sees.
  const roles = rolesQuery.data ?? []
  const defaultRoleId = (roles.find((r) => r.title === 'Tenant Admin') ?? roles.find((r) => r.hidden) ?? roles[0])?.id ?? ''
  const selectedRoleId = roleId || defaultRoleId

  const mutation = useMutation({
    mutationFn: async (kind: OrgActionKind) => {
      if (kind === 'support_access') {
        return { credentials: await masterAdminApi.supportAccess(org.id, reason.trim(), selectedRoleId) }
      }
      await masterAdminApi.changeOrgStatus(org.id, kind, { reason: reason.trim(), confirm_name: confirmName.trim() })
      return { credentials: undefined }
    },
    onSuccess: ({ credentials }, kind) => {
      void queryClient.invalidateQueries({ queryKey: ['master-admin'] })
      onDone(credentials ? 'Support access generated.' : `${org.name}: ${ORG_ACTIONS[kind].label.toLowerCase()} done.`, credentials)
    },
  })

  if (!action) return null
  const meta = ORG_ACTIONS[action]
  const needsName = action === 'terminate'
  const canSubmit = reason.trim().length >= 5
    && (!needsName || confirmName.trim().toLowerCase() === org.name.trim().toLowerCase())
    && (action !== 'support_access' || Boolean(selectedRoleId))

  return (
    <Dialog open onClose={mutation.isPending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{meta.title}: {org.name}</DialogTitle>
      <DialogContent>
        <DialogContentText>{meta.explain}</DialogContentText>
        <TextField
          label="Reason (saved in the audit log)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          margin="normal"
          autoFocus
          helperText="At least 5 characters."
        />
        {action === 'support_access' && (
          <TextField
            select
            label="Sign in as role"
            value={selectedRoleId}
            onChange={(e) => setRoleId(e.target.value)}
            fullWidth
            margin="dense"
            disabled={rolesQuery.isLoading}
            helperText={rolesQuery.isLoading ? 'Loading roles…' : roles.length === 0 ? 'This organization has no roles to assign.' : undefined}
          >
            {roles.map((r) => <MenuItem key={r.id} value={r.id}>{r.title || 'Untitled role'}</MenuItem>)}
          </TextField>
        )}
        {needsName && (
          <TextField
            label={`Type "${org.name}" to confirm`}
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            fullWidth
            margin="dense"
          />
        )}
        {mutation.error && <Alert severity="error" sx={{ mt: 1 }}>{getErrorMessage(mutation.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
        <Button
          variant="contained"
          color={meta.color}
          startIcon={mutation.isPending ? <CircularProgress size={16} /> : meta.icon}
          disabled={!canSubmit || mutation.isPending}
          onClick={() => mutation.mutate(action)}
        >
          {meta.label}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function CredentialsDialog({ credentials, onClose, notify }: {
  credentials: SupportAccessCredentials | null
  onClose: () => void
  notify: (message: string) => void
}) {
  if (!credentials) return null
  const copy = (value: string, what: string) => {
    void navigator.clipboard?.writeText(value).then(() => notify(`${what} copied`), () => notify(`Copy failed; select the ${what.toLowerCase()} and copy it by hand`))
  }
  const rows: [string, string][] = [['Business code', credentials.tenant], ['Username', credentials.username], ['Password', credentials.password]]
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Support access</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This password is shown only once and isn't stored anywhere. Generating support access again replaces it.
        </Alert>
        {rows.map(([label, value]) => (
          <Stack key={label} direction="row" alignItems="center" justifyContent="space-between" className="info">
            <Typography className="muted">{label}</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography fontWeight={700} sx={{ fontFamily: label === 'Password' ? 'monospace' : undefined }}>{value}</Typography>
              <IconButton size="small" onClick={() => copy(value, label)}><ContentCopyRounded fontSize="small" /></IconButton>
            </Stack>
          </Stack>
        ))}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  )
}

function AuditEntries({ entries, showOrganization }: { entries: MasterAdminAuditEntry[]; showOrganization?: boolean }) {
  if (entries.length === 0) return <Empty>No admin actions recorded yet.</Empty>
  return (
    <>
      {entries.map((e) => (
        <Box key={e.id} className="info auditEntry">
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box>
              <Typography fontWeight={700}>
                {e.action_label}
                {e.action.startsWith('staff_') && e.details.member ? ` · ${String(e.details.member)}` : ''}
                {showOrganization && e.organization_name ? ` · ${e.organization_name}` : ''}
              </Typography>
              <Typography className="muted" mt={0.5}>“{e.reason}”</Typography>
              {e.action === 'pricing_update' && ((e.details.changes ?? []) as string[]).map((line) => (
                <Typography key={line} className="tiny" mt={0.5}>{line}</Typography>
              ))}
              {e.action === 'settings_update' && Object.entries((e.details.changes ?? {}) as Record<string, [unknown, unknown]>).map(([name, [before, after]]) => (
                <Typography key={name} className="tiny" mt={0.5}>
                  {SETTING_LABELS[name] ?? name}: {Array.isArray(before) ? before.join(', ') || 'none' : String(before)} → {Array.isArray(after) ? after.join(', ') || 'none' : String(after)}
                </Typography>
              ))}
              <Typography className="tiny" mt={0.5}>
                {e.actor_name || 'Unknown admin'}
                {e.previous_status && e.new_status ? ` · ${e.previous_status} → ${e.new_status}` : ''}
                {e.ip_address ? ` · ${e.ip_address}` : ''}
              </Typography>
            </Box>
            <Typography className="tiny" sx={{ whiteSpace: 'nowrap' }}>{formatTime(e.created_at)}</Typography>
          </Stack>
        </Box>
      ))}
    </>
  )
}

function AuditLogPage({ query }: { query: string }) {
  const auditQuery = useQuery({ queryKey: masterAdminQueryKeys.auditLog(), queryFn: () => masterAdminApi.auditLog() })
  const rows = (auditQuery.data ?? []).filter((r) => matches(r, query))
  return (
    <>
      <PageTitle title="Audit Log" sub="Every org action, platform settings change and team change, with who did it and why. Latest 200." />
      <QueryGate isLoading={auditQuery.isLoading} error={auditQuery.error}>
        <Paper className="card"><AuditEntries entries={rows} showOrganization /></Paper>
      </QueryGate>
    </>
  )
}

function OrgActivity({ orgId }: { orgId: number }) {
  const auditQuery = useQuery({ queryKey: masterAdminQueryKeys.auditLog(orgId), queryFn: () => masterAdminApi.auditLog(orgId) })
  return (
    <Paper className="card span2">
      <Typography fontWeight={800}>Admin activity</Typography>
      <QueryGate isLoading={auditQuery.isLoading} error={auditQuery.error}>
        <AuditEntries entries={auditQuery.data ?? []} />
      </QueryGate>
    </Paper>
  )
}

function OrganizationDetail({ org, requests, setPage, openRequest, notify }: {
  org: MasterAdminOrganization | undefined
  requests: HelpSupportRequest[]
  setPage: (p: PageName) => void
  openRequest: (id: number) => void
  notify: (message: string) => void
}) {
  const can = useCan()
  const [tab, setTab] = useState(0)
  const [action, setAction] = useState<OrgActionKind | null>(null)
  const [credentials, setCredentials] = useState<SupportAccessCredentials | null>(null)
  if (!org) {
    return <PageTitle title="Organization" sub="Select an organization first." action={<Button onClick={() => setPage('Organizations')}>Back</Button>} />
  }
  const orgRequests = requests.filter((r) => r.organization?.instance_id === org.instance_id)
  const { billing } = org
  return (
    <>
      <PageTitle
        title={org.name}
        sub={`${shortVertical(org.vertical)} • ${org.country || '—'} • Customer since ${formatDate(org.created_on)}`}
        action={<Button onClick={() => setPage('Organizations')}>Back</Button>}
      />
      <Paper className="card">
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 54, height: 54 }}>{initials(org.name)}</Avatar>
          <Box flex={1}>
            <Typography variant="h6" fontWeight={800}>{org.name}</Typography>
            <Stack direction="row" spacing={1}>
              <PlanChip org={org} />
              <Chip size="small" color={ORG_STATUS_COLOR[org.status] ?? 'default'} label={org.status} />
            </Stack>
          </Box>
          <Box textAlign="right">
            <Typography className="tiny">LICENCES</Typography>
            <Typography variant="h6" fontWeight={800}>{billing.user_count}</Typography>
            {billing.admin_count !== null ? (
              <Typography className="tiny">{billing.admin_count} admin • {billing.worker_count} field worker</Typography>
            ) : null}
          </Box>
        </Stack>
        {org.status_code !== ORG_STATUS_CODE.approved && org.status_code !== ORG_STATUS_CODE.pending && (
          <Alert severity={org.status_code === ORG_STATUS_CODE.suspended ? 'warning' : 'error'} sx={{ mt: 2 }}>
            {org.status_code === ORG_STATUS_CODE.suspended
              ? 'Suspended: nobody in this organization can sign in or use the API.'
              : `${org.status}: nobody in this organization can sign in.`}
          </Alert>
        )}
        <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
          {availableOrgActions(org.status_code).filter((kind) => can(ORG_ACTION_PERMISSION[kind])).map((kind) => (
            <Button
              key={kind}
              size="small"
              variant={kind === 'terminate' ? 'text' : 'outlined'}
              color={ORG_ACTIONS[kind].color}
              startIcon={ORG_ACTIONS[kind].icon}
              onClick={() => setAction(kind)}
            >
              {ORG_ACTIONS[kind].label}
            </Button>
          ))}
        </Stack>
        <Tabs value={tab} onChange={(_, v: number) => setTab(v)} sx={{ mt: 2 }} variant="scrollable">
          <Tab label="Overview" /><Tab label="Subscription" /><Tab label={`Support (${orgRequests.length})`} /><Tab label="Activity" />
        </Tabs>
      </Paper>
      <OrgActionDialog
        org={org}
        action={action}
        onClose={() => setAction(null)}
        onDone={(message, generated) => { setAction(null); notify(message); if (generated) setCredentials(generated) }}
      />
      <CredentialsDialog credentials={credentials} onClose={() => setCredentials(null)} notify={notify} />
      <Box className="detailGrid">
        {tab === 0 && (
          <>
            <Paper className="card">
              <Typography fontWeight={800}>Organization profile</Typography>
              <Info k="Owner" v={org.owner_name || '—'} />
              <Info k="Email" v={org.owner_email || '—'} />
              <Info k="Country" v={org.country || '—'} />
              <Info k="Vertical" v={shortVertical(org.vertical)} />
              <Info k="Instance ID" v={org.instance_id} />
            </Paper>
            <Paper className="card">
              <Typography fontWeight={800}>Usage snapshot</Typography>
              <Info k="Staff seats" v={billing.user_count} />
              <Info k="Branches" v={org.branch_count} />
              <Info k="Account status" v={org.status} />
              <Info k="Joined" v={formatDate(org.created_on)} />
            </Paper>
          </>
        )}
        {tab === 1 && (
          <Paper className="card span2">
            <Typography fontWeight={800}>Subscription</Typography>
            <Info k="Plan" v={PLAN_META[billing.plan]?.label ?? billing.plan} />
            <Info k="Subscription status" v={billing.subscription_status ?? '—'} />
            <Info k="Trial started" v={formatDate(billing.trial_start_on)} />
            <Info k="Trial ends" v={formatDate(billing.trial_end_on)} />
            <Info k="Trial days remaining" v={billing.trial_days_remaining ?? '—'} />
            <Info k="Locked" v={billing.is_locked ? `Yes (${billing.lock_reason ?? 'unknown'})` : 'No'} />
          </Paper>
        )}
        {tab === 2 && (
          <Paper className="card span2">
            <Typography fontWeight={800}>Support requests</Typography>
            {orgRequests.length === 0 ? <Empty>This organization has not raised any support requests.</Empty> : orgRequests.map((r) => (
              <Stack key={r.id} direction="row" justifyContent="space-between" alignItems="center" className="info">
                <Box>
                  <Typography fontWeight={700}>{r.subject}</Typography>
                  <Typography className="tiny">{r.reference} · {formatTime(r.updated_at)}</Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" color={SUPPORT_STATUS[r.status]?.color} label={SUPPORT_STATUS[r.status]?.label ?? r.status} />
                  <Button size="small" onClick={() => openRequest(r.id)}>Open</Button>
                </Stack>
              </Stack>
            ))}
          </Paper>
        )}
        {tab === 3 && <OrgActivity orgId={org.id} />}
      </Box>
    </>
  )
}

function InvoicesPage({ query }: { query: string }) {
  const invoicesQuery = useQuery({ queryKey: masterAdminQueryKeys.invoices, queryFn: masterAdminApi.invoices })
  const rows = (invoicesQuery.data?.invoices ?? []).filter((r) => matches(r, query))
  return (
    <>
      <PageTitle title="Invoices" sub="Platform subscription invoices from the FacilityPro Stripe account." />
      <QueryGate isLoading={invoicesQuery.isLoading} error={invoicesQuery.error}>
        {invoicesQuery.data && !invoicesQuery.data.configured ? (
          <Alert severity="info">
            Platform Stripe billing is not configured for this environment (STRIPE_PLATFORM_SECRET_KEY is empty), so there are no invoices to show.
          </Alert>
        ) : (
          <Paper className="card tableCard">
            <Box className="table invoiceTable head">
              {['NUMBER', 'ORGANIZATION', 'DATE', 'DUE', 'AMOUNT', 'STATUS', ''].map((x, i) => <span key={`${x}-${i}`}>{x}</span>)}
            </Box>
            {rows.length === 0 ? <Empty>No invoices yet.</Empty> : rows.map((r) => (
              <Box className="table invoiceTable row" key={r.id}>
                <strong>{r.number}</strong>
                <span>{r.organization || '—'}</span>
                <span>{formatDate(r.created)}</span>
                <span>{formatDate(r.due_date)}</span>
                <strong>{formatMoney(r.amount_due, r.currency)}</strong>
                <Chip size="small" color={r.status === 'paid' ? 'success' : r.status === 'open' ? 'warning' : 'default'} label={r.status ?? '—'} />
                {r.hosted_invoice_url
                  ? <IconButton size="small" href={r.hosted_invoice_url} target="_blank" rel="noopener noreferrer"><OpenInNewRounded fontSize="small" /></IconButton>
                  : <span />}
              </Box>
            ))}
          </Paper>
        )}
      </QueryGate>
    </>
  )
}

function SupportInbox({ overview, query, activeId, setActiveId, notify }: {
  overview: MasterAdminOverview
  query: string
  activeId: number | null
  setActiveId: (id: number | null) => void
  notify: (m: string) => void
}) {
  const queryClient = useQueryClient()
  const canReply = useCan()('support_reply')
  const supportQuery = useQuery({ queryKey: masterAdminQueryKeys.support, queryFn: masterAdminApi.supportList })
  const [text, setText] = useState('')
  const [files, setFiles] = useState<File[]>([])
  // A reply is emailed to the customer; an internal note stays with the team.
  const [composeKind, setComposeKind] = useState<'reply' | 'note'>('reply')
  const [assigneeFilter, setAssigneeFilter] = useState<'all' | 'mine' | 'unassigned'>('all')
  const requests = supportQuery.data?.requests ?? []
  const assignees = supportQuery.data?.assignees ?? []
  const myUserId = supportQuery.data?.me ?? null
  const filtered = requests.filter((r) => matches(r, query) && (
    assigneeFilter === 'all'
    || (assigneeFilter === 'mine' ? r.assigned_to?.id === myUserId : !r.assigned_to)
  ))
  const active = requests.find((r) => r.id === activeId) ?? filtered[0]
  const rootInstanceId = overview.root_org.instance_id

  useEffect(() => { setText(''); setFiles([]); setComposeKind('reply') }, [active?.id])

  const onSaved = (updated: HelpSupportRequest) => {
    queryClient.setQueryData<MasterAdminSupportList>(masterAdminQueryKeys.support, (prev) => (prev
      ? { ...prev, requests: prev.requests.map((r) => (r.id === updated.id ? updated : r)) }
      : prev))
    void queryClient.invalidateQueries({ queryKey: masterAdminQueryKeys.overview })
  }
  const replyMutation = useMutation({
    mutationFn: ({ id, kind, body, attachments }: { id: number; kind: 'reply' | 'note'; body: string; attachments: File[] }) =>
      masterAdminApi.supportMessage(id, kind, body, attachments),
    onSuccess: (updated, { kind }) => {
      onSaved(updated)
      setText('')
      setFiles([])
      if (kind === 'note') notify('Internal note added. The customer can\'t see it.')
      else notify(updated.emailed_to ? `Reply sent and emailed to ${updated.emailed_to}` : 'Reply saved, but the customer could not be emailed')
    },
    onError: (error) => notify(getErrorMessage(error, 'Unable to save the message.')),
  })
  const assignMutation = useMutation({
    mutationFn: ({ id, userId }: { id: number; userId: number | null }) => masterAdminApi.supportAssign(id, userId),
    onSuccess: (updated) => {
      onSaved(updated)
      notify(updated.assigned_to ? `Assigned to ${updated.assigned_to.name}` : 'Unassigned')
    },
    onError: (error) => notify(getErrorMessage(error, 'Unable to assign the request.')),
  })
  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: HelpSupportStatus }) => masterAdminApi.supportUpdate(id, status),
    onSuccess: (updated) => {
      onSaved(updated)
      const label = SUPPORT_STATUS[updated.status]?.label ?? updated.status
      notify(updated.emailed_to ? `Marked as ${label} and emailed ${updated.emailed_to}` : `Marked as ${label}`)
    },
    onError: (error) => notify(getErrorMessage(error, 'Unable to update the request.')),
  })

  const send = () => {
    if (!active || (!text.trim() && files.length === 0) || replyMutation.isPending) return
    replyMutation.mutate({ id: active.id, kind: composeKind, body: text.trim(), attachments: files })
  }
  const org = overview.organizations.find((o) => o.instance_id === active?.organization?.instance_id)
  const openCount = requests.filter((r) => r.status === 'open' || r.status === 'in_progress').length

  return (
    <>
      <PageTitle
        title="Support Inbox"
        sub="Help requests raised by FacilityPro customers."
        action={<Chip color="primary" variant="outlined" label={`${openCount} open`} />}
      />
      <QueryGate isLoading={supportQuery.isLoading} error={supportQuery.error}>
        {requests.length === 0 ? (
          <Paper className="card"><Empty>No support requests yet. Requests raised from a customer's Help &amp; Support page appear here.</Empty></Paper>
        ) : (
          <Paper className="supportInbox">
            <Box className="conversationList">
              <Box className="conversationListHeader">
                <Typography fontWeight={800}>Requests</Typography>
                <Typography className="muted">{requests.length} total</Typography>
              </Box>
              <Stack direction="row" spacing={0.8} px={2} py={1.2} sx={{ borderBottom: '1px solid #f1f3f6' }}>
                {([['all', 'All'], ['mine', 'Assigned to me'], ['unassigned', 'Unassigned']] as const).map(([value, label]) => (
                  <Chip
                    key={value}
                    size="small"
                    label={label}
                    color={assigneeFilter === value ? 'primary' : 'default'}
                    variant={assigneeFilter === value ? 'filled' : 'outlined'}
                    onClick={() => setAssigneeFilter(value)}
                  />
                ))}
              </Stack>
              {filtered.length === 0 && <Box p={2}><Typography className="muted">No requests match.</Typography></Box>}
              {filtered.map((r) => (
                <Box key={r.id} className={`conversationItem ${active?.id === r.id ? 'active' : ''}`} onClick={() => setActiveId(r.id)}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography fontWeight={800} fontSize={13}>
                      {r.status === 'open' && <span className="unreadDot" />}
                      {r.organization?.name ?? 'Unknown organization'}
                    </Typography>
                    <Typography className="tiny">{formatDate(r.updated_at)}</Typography>
                  </Stack>
                  <Typography fontSize={12.5} fontWeight={650} mt={0.7}>{r.subject}</Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
                    <Typography className="tiny">
                      {r.reference} · {r.replies.filter((reply) => !reply.is_internal).length} replies
                      {r.assigned_to ? ` · ${r.assigned_to.name}` : ' · Unassigned'}
                    </Typography>
                    <Chip size="small" color={SUPPORT_STATUS[r.status]?.color} label={SUPPORT_STATUS[r.status]?.label ?? r.status} />
                  </Stack>
                </Box>
              ))}
            </Box>
            {active && (
              <Box className="chatPane">
                <Box className="chatHeader">
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography fontWeight={800}>{active.subject}</Typography>
                      <Typography className="tiny">{active.organization?.name} · {active.reference}</Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        select
                        size="small"
                        label="Assigned to"
                        value={active.assigned_to?.id ?? ''}
                        disabled={!canReply || assignMutation.isPending}
                        onChange={(e) => assignMutation.mutate({ id: active.id, userId: e.target.value === '' ? null : Number(e.target.value) })}
                        sx={{ minWidth: 170 }}
                        SelectProps={{ displayEmpty: true }}
                        InputLabelProps={{ shrink: true }}
                      >
                        <MenuItem value="">Unassigned</MenuItem>
                        {assignees.map((person) => (
                          <MenuItem key={person.id} value={person.id}>{person.name}{person.id === myUserId ? ' (you)' : ''}</MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        size="small"
                        label="Status"
                        value={active.status}
                        disabled={!canReply || statusMutation.isPending}
                        onChange={(e) => statusMutation.mutate({ id: active.id, status: e.target.value as HelpSupportStatus })}
                        sx={{ minWidth: 140 }}
                      >
                        {(Object.keys(SUPPORT_STATUS) as HelpSupportStatus[]).map((s) => <MenuItem key={s} value={s}>{SUPPORT_STATUS[s].label}</MenuItem>)}
                      </TextField>
                    </Stack>
                  </Stack>
                </Box>
                <Box className="chatMessages">
                  <Box className="bubble customer">
                    <div>{active.message}</div>
                    <HelpAttachmentLinks attachments={active.attachments} />
                    <div className="bubbleTime">{active.organization?.name} · {formatTime(active.created_at)}</div>
                  </Box>
                  {active.replies.map((reply) => {
                    const fromSupport = reply.organization?.instance_id === rootInstanceId
                    if (reply.is_internal) {
                      return (
                        <Box key={reply.id} className="bubble note">
                          <div className="noteLabel">Internal note · not visible to the customer</div>
                          {reply.body && <div>{reply.body}</div>}
                          <HelpAttachmentLinks attachments={reply.attachments} />
                          <div className="bubbleTime">{reply.author_name || 'FacilityPro Support'} · {formatTime(reply.created_at)}</div>
                        </Box>
                      )
                    }
                    return (
                      <Box key={reply.id} className={`bubble ${fromSupport ? 'support' : 'customer'}`}>
                        {reply.body && <div>{reply.body}</div>}
                        <HelpAttachmentLinks attachments={reply.attachments} />
                        <div className="bubbleTime">
                          {fromSupport ? `FacilityPro Support${reply.author_name ? ` (${reply.author_name})` : ''}` : (reply.author_name || reply.organization?.name)} · {formatTime(reply.created_at)}
                        </div>
                      </Box>
                    )
                  })}
                </Box>
                {!canReply ? (
                  <Box className="chatComposer"><Typography className="muted">Your role can read support requests but not reply.</Typography></Box>
                ) : (
                <Box className={`chatComposer stacked ${composeKind === 'note' ? 'noteMode' : ''}`}>
                  <Stack direction="row" spacing={0.8}>
                    <Chip size="small" label="Reply to customer" color={composeKind === 'reply' ? 'primary' : 'default'} variant={composeKind === 'reply' ? 'filled' : 'outlined'} onClick={() => setComposeKind('reply')} />
                    <Chip size="small" label="Internal note" color={composeKind === 'note' ? 'warning' : 'default'} variant={composeKind === 'note' ? 'filled' : 'outlined'} onClick={() => setComposeKind('note')} />
                  </Stack>
                  <Stack direction="row" spacing={1.1} alignItems="flex-end">
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      placeholder={composeKind === 'note' ? 'Note for the team — the customer won\'t see this…' : 'Type a reply…'}
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
                    />
                    <Button
                      variant="contained"
                      color={composeKind === 'note' ? 'warning' : 'primary'}
                      endIcon={<SendRounded />}
                      onClick={send}
                      disabled={(!text.trim() && files.length === 0) || replyMutation.isPending}
                    >
                      {composeKind === 'note' ? 'Add note' : 'Send'}
                    </Button>
                  </Stack>
                  <HelpAttachmentPicker files={files} onChange={setFiles} onError={notify} disabled={replyMutation.isPending} />
                </Box>
                )}
              </Box>
            )}
            {active && (
              <Box className="customerPanel">
                <Typography fontWeight={800}>Customer</Typography>
                <Stack direction="row" spacing={1.2} alignItems="center" mt={2}>
                  <Avatar>{initials(active.organization?.name ?? '?')}</Avatar>
                  <Box>
                    <Typography fontWeight={800} fontSize={13}>{active.organization?.name}</Typography>
                    <Typography className="tiny">{org ? `${PLAN_META[org.billing.plan]?.label} plan` : 'Customer'}</Typography>
                  </Box>
                </Stack>
                <Box mt={2}>
                  <Info k="Raised by" v={active.raised_by?.name || '—'} />
                  <Info k="Replies emailed to" v={active.raised_by?.email || org?.owner_email || '—'} />
                  <Info k="Org owner" v={org?.owner_name || '—'} />
                  <Info k="Vertical" v={org ? shortVertical(org.vertical) : '—'} />
                  <Info k="Users" v={org?.billing.user_count ?? '—'} />
                </Box>
                {canReply && <Button
                  fullWidth
                  color="success"
                  startIcon={<CheckCircleOutlineRounded />}
                  sx={{ mt: 2 }}
                  disabled={active.status === 'resolved' || statusMutation.isPending}
                  onClick={() => statusMutation.mutate({ id: active.id, status: 'resolved' })}
                >
                  Mark resolved
                </Button>}
              </Box>
            )}
          </Paper>
        )}
      </QueryGate>
    </>
  )
}

const hasAmount = (price: PricingPrice | undefined) => Boolean(price && price.amount !== null && String(price.amount).trim() !== '')

function priceText(price: PricingPrice | undefined, symbol: string) {
  if (!price) return '—'
  return hasAmount(price) ? `${symbol}${Number(price.amount).toLocaleString()}` : (price.price_label || '—')
}

const BILLING_ROLE_LABEL: Record<PricingBillingRole, string> = {
  '': 'Display only',
  tier: 'Billing plan',
}

type PlanAmount = 'amount' | 'worker_amount' | 'base_amount' | 'minimum_amount'
/** A billing plan's amounts per currency, a month. */
const PLAN_AMOUNTS: { field: PlanAmount; label: string }[] = [
  { field: 'amount', label: 'Admin / Manager' },
  { field: 'worker_amount', label: 'Field Worker' },
  { field: 'base_amount', label: 'Plan fee' },
  { field: 'minimum_amount', label: 'Minimum' },
]

const filled = (value: string | null | undefined) => value !== null && value !== undefined && String(value).trim() !== ''
const money = (symbol: string, value: string | null | undefined) => `${symbol}${Number(value).toLocaleString()}`

/** What a billing plan still needs in a currency before organizations on it can be billed. */
function planMissing(pkg: PricingPackageRow, code: string) {
  const price = pkg.prices[code]
  const missing: string[] = []
  if (!filled(price?.amount)) missing.push('Admin / Manager price')
  if (!filled(price?.worker_amount)) missing.push('Field Worker price')
  if (pkg.included_users > 0 && !filled(price?.base_amount)) missing.push('plan fee')
  return missing
}

const teamSize = (pkg: PricingPackageRow) =>
  pkg.min_users === null ? '' : pkg.max_users === null ? `${pkg.min_users}+ users` : `${pkg.min_users}–${pkg.max_users} users`

/** A billing plan's headline price (the plan fee when it has one) and the lines under it. */
function planSummary(pkg: PricingPackageRow, currency: PricingCurrencyRow) {
  const price = pkg.prices[currency.code]
  const fee = filled(price?.base_amount)
  const headline = fee ? money(currency.symbol, price?.base_amount) : filled(price?.amount) ? money(currency.symbol, price?.amount) : '—'
  const suffix = fee ? '/ month' : filled(price?.amount) ? '/ admin / month' : ''
  const lines: string[] = []
  if (fee && pkg.included_users) lines.push(`Includes ${pkg.included_users} users`)
  if (filled(price?.amount) && fee) lines.push(`+ ${money(currency.symbol, price?.amount)} per additional admin`)
  if (filled(price?.worker_amount)) lines.push(`${fee ? '+ ' : ''}${money(currency.symbol, price?.worker_amount)} per ${fee ? 'additional ' : ''}field worker`)
  if (filled(price?.minimum_amount)) lines.push(`${money(currency.symbol, price?.minimum_amount)} minimum a month`)
  return { headline, suffix, lines }
}

const emptyPackage = (currencies: PricingCurrencyRow[]): PricingPackageRow => ({
  key: '', user_range_label: '', min_users: null, max_users: null, included_users: 0, title: '', description: '', features: [],
  price_suffix: '/ user / month', cta_label: '', cta_href: '', style: 'standard', billing_role: '', is_active: true,
  prices: Object.fromEntries(currencies.map((c) => [c.code, { amount: '', price_label: '' }])),
})

const CONTACT_US_LABEL = 'Let’s talk'
const CHARGE_OPTIONS = [
  { value: '/ user / month', label: 'Per user, per month' },
  { value: '/ month', label: 'Per month (flat)' },
]

const slugify = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'package'

/**
 * A simple package editor: everything else (key, card style, button) follows
 * from these fields. A billing plan is also what customers are charged: the
 * whole team pays the rates of the plan its size falls in. Its amounts may
 * stay blank until they're decided.
 */
function PackageDialog({ initial, isNew, currencies, existingKeys, onClose, onSave }: {
  initial: PricingPackageRow | null
  isNew: boolean
  currencies: PricingCurrencyRow[]
  existingKeys: string[]
  onClose: () => void
  onSave: (pkg: PricingPackageRow) => void
}) {
  const [pkg, setPkg] = useState<PricingPackageRow | null>(initial)
  const [contactUs, setContactUs] = useState(false)
  const [featuresText, setFeaturesText] = useState('')
  useEffect(() => {
    setPkg(initial)
    setFeaturesText((initial?.features ?? []).join('\n'))
    // Billing cards are never "Contact us"; blank just means not priced yet.
    setContactUs(Boolean(initial && !isNew && !initial.billing_role && currencies.length > 0 && currencies.every((c) => !hasAmount(initial.prices[c.code]))))
  }, [initial, isNew, currencies])
  if (!pkg) return null

  const set = <K extends keyof PricingPackageRow>(key: K, value: PricingPackageRow[K]) => setPkg((p) => (p ? { ...p, [key]: value } : p))
  const setAmount = (code: string, value: string, field: PlanAmount = 'amount') =>
    setPkg((p) => (p ? { ...p, prices: { ...p.prices, [code]: { ...p.prices[code], [field]: value.replace(/[^0-9.]/g, ''), price_label: '' } } } : p))
  const setUsers = (key: 'min_users' | 'max_users' | 'included_users', value: string) => {
    const digits = value.replace(/[^0-9]/g, '')
    setPkg((p) => (p ? { ...p, [key]: key === 'included_users' ? Number(digits || 0) : digits === '' ? null : Number(digits) } : p))
  }
  const chargeOptions = CHARGE_OPTIONS.some((o) => o.value === pkg.price_suffix) || !pkg.price_suffix
    ? CHARGE_OPTIONS
    : [...CHARGE_OPTIONS, { value: pkg.price_suffix, label: pkg.price_suffix }]
  const billingRole = pkg.billing_role
  const isBilling = billingRole !== ''
  // Plan amounts may stay blank (that currency just can't be billed yet), but a plan needs its team size.
  const missing = !pkg.title.trim() || !pkg.user_range_label.trim()
    || (isBilling && pkg.min_users === null)
    || (!isBilling && !contactUs && currencies.some((c) => !hasAmount(pkg.prices[c.code])))
  const setBillingRole = (role: PricingBillingRole) => {
    setPkg((p) => (p ? { ...p, billing_role: role, price_suffix: role ? '/ user / month' : p.price_suffix, included_users: role ? p.included_users : 0 } : p))
    if (role) setContactUs(false)
  }

  const apply = () => {
    const prices = Object.fromEntries(currencies.map((c) => [
      c.code,
      contactUs
        ? { amount: '', price_label: CONTACT_US_LABEL }
        : isBilling
          ? { ...pkg.prices[c.code], amount: pkg.prices[c.code]?.amount ?? '', price_label: '' }
          : { amount: pkg.prices[c.code]?.amount ?? '', price_label: '' },
    ]))
    const free = !contactUs && !isBilling && currencies.every((c) => Number(pkg.prices[c.code]?.amount) === 0)
    const highlighted = pkg.style === 'featured'
    // A package that switches between a price and "Contact us" gets that mode's button.
    const wasContactUs = Boolean(initial && !isNew && !initial.billing_role && currencies.every((c) => !hasAmount(initial.prices[c.code])))
    const keepButton = !isNew && contactUs === wasContactUs
    let key = pkg.key
    if (isNew) {
      key = slugify(pkg.title)
      for (let n = 2; existingKeys.includes(key); n += 1) key = `${slugify(pkg.title).slice(0, 36)}-${n}`
    }
    onSave({
      ...pkg,
      key,
      title: pkg.title.trim(),
      user_range_label: pkg.user_range_label.trim().toUpperCase(),
      min_users: isBilling ? pkg.min_users : null,
      max_users: isBilling ? pkg.max_users : null,
      included_users: isBilling ? pkg.included_users : 0,
      prices,
      style: highlighted ? 'featured' : contactUs ? 'enterprise' : free ? 'free' : 'standard',
      cta_label: keepButton && pkg.cta_label ? pkg.cta_label : (contactUs ? 'Discuss pricing' : 'Start 30-day free trial'),
      cta_href: keepButton && pkg.cta_href ? pkg.cta_href : (contactUs ? '/book-demo' : '/signup'),
    })
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isNew ? 'Add package' : `Edit ${initial?.title}`}</DialogTitle>
      <DialogContent>
        <TextField label="Name" value={pkg.title} onChange={(e) => set('title', e.target.value)} fullWidth margin="dense" required autoFocus={isNew} />

        <TextField
          label="Card label"
          value={pkg.user_range_label}
          onChange={(e) => set('user_range_label', e.target.value)}
          fullWidth
          margin="dense"
          required
          helperText="Small heading above the name, e.g. GROWTH · 11–50 USERS."
        />
        <TextField
          select
          label="Billing"
          value={billingRole}
          onChange={(e) => setBillingRole(e.target.value as PricingBillingRole)}
          fullWidth
          margin="dense"
          helperText={isBilling
            ? 'Organizations whose team size is in this plan’s range pay its rates for every user. Leave a currency blank until its price is decided; organizations billed in it can’t subscribe until then.'
            : 'Shown on the website only; nobody is charged from this card.'}
        >
          {(Object.keys(BILLING_ROLE_LABEL) as PricingBillingRole[]).map((role) => (
            <MenuItem key={role || 'none'} value={role}>{BILLING_ROLE_LABEL[role]}</MenuItem>
          ))}
        </TextField>
        {isBilling && (
          <Stack direction="row" spacing={1.5} mt={1} flexWrap="wrap" useFlexGap>
            <TextField label="From (users)" value={pkg.min_users ?? ''} onChange={(e) => setUsers('min_users', e.target.value)} required sx={{ width: 140 }} />
            <TextField label="To (users)" value={pkg.max_users ?? ''} onChange={(e) => setUsers('max_users', e.target.value)} helperText="Blank = no limit" sx={{ width: 140 }} />
            <TextField
              label="Included users"
              value={pkg.included_users || ''}
              onChange={(e) => setUsers('included_users', e.target.value)}
              helperText="Covered by the plan fee"
              sx={{ width: 160 }}
            />
          </Stack>
        )}

        <Typography fontWeight={700} mt={2}>Price</Typography>
        {!isBilling && (
          <RadioGroup row value={contactUs ? 'contact' : 'fixed'} onChange={(e) => setContactUs(e.target.value === 'contact')}>
            <FormControlLabel value="fixed" control={<Radio />} label="Fixed price" />
            <FormControlLabel value="contact" control={<Radio />} label={`Contact us (shows “${CONTACT_US_LABEL}”)`} />
          </RadioGroup>
        )}
        {isBilling && (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: `60px repeat(${PLAN_AMOUNTS.length}, 1fr)`, gap: 1, alignItems: 'center', mt: 1 }}>
              <span />
              {PLAN_AMOUNTS.map((a) => <Typography key={a.field} className="tiny">{a.label}</Typography>)}
              {currencies.map((c) => (
                <Fragment key={c.code}>
                  <Typography fontWeight={700}>{c.code}</Typography>
                  {PLAN_AMOUNTS.map((a) => (
                    <TextField
                      key={a.field}
                      size="small"
                      aria-label={`${c.code} ${a.label}`}
                      value={pkg.prices[c.code]?.[a.field] ?? ''}
                      onChange={(e) => setAmount(c.code, e.target.value, a.field)}
                      slotProps={{ input: { startAdornment: <InputAdornment position="start">{c.symbol}</InputAdornment> } }}
                    />
                  ))}
                </Fragment>
              ))}
            </Box>
            <Typography className="tiny" mt={1}>
              Per user a month (Admin / Manager, Field Worker), or a month (plan fee, minimum). The plan fee covers the included
              users, Field Workers first; leave it blank for none. The minimum is optional. Annual plans pay 12 months less the
              annual discount, up front.
            </Typography>
          </>
        )}
        {!contactUs && !isBilling && (
          <>
            <Stack direction="row" spacing={1.5} mt={1} flexWrap="wrap" useFlexGap>
              {currencies.map((c) => (
                <TextField
                  key={c.code}
                  label={c.code}
                  value={pkg.prices[c.code]?.amount ?? ''}
                  onChange={(e) => setAmount(c.code, e.target.value)}
                  slotProps={{ input: { startAdornment: <InputAdornment position="start">{c.symbol}</InputAdornment> } }}
                  sx={{ width: 150 }}
                  required
                />
              ))}
            </Stack>
            <TextField select label="Charged" value={pkg.price_suffix || CHARGE_OPTIONS[0].value} onChange={(e) => set('price_suffix', e.target.value)} margin="normal" sx={{ minWidth: 260 }}>
              {chargeOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          </>
        )}

        <TextField label="Description" value={pkg.description} onChange={(e) => set('description', e.target.value)} margin="normal" fullWidth multiline minRows={2} />
        <TextField
          label="Features"
          placeholder={'One per line, e.g.\nUnlimited jobs\nWorker mobile app'}
          value={featuresText}
          onChange={(e) => { setFeaturesText(e.target.value); set('features', e.target.value.split('\n').map((f) => f.trim()).filter(Boolean)) }}
          margin="normal"
          fullWidth
          multiline
          minRows={3}
        />
        <Stack direction="row" spacing={3} mt={1}>
          <FormControlLabel control={<Switch checked={pkg.is_active} onChange={(e) => set('is_active', e.target.checked)} />} label="Show on website" />
          <FormControlLabel
            control={<Switch checked={pkg.style === 'featured'} onChange={(e) => set('style', e.target.checked ? 'featured' : 'standard')} />}
            label="Highlight as most popular"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={missing} onClick={apply}>{isNew ? 'Add' : 'Apply'}</Button>
      </DialogActions>
    </Dialog>
  )
}

function CurrenciesDialog({ open, initial, onClose, onSave }: {
  open: boolean
  initial: PricingCurrencyRow[]
  onClose: () => void
  onSave: (currencies: PricingCurrencyRow[]) => void
}) {
  const [rows, setRows] = useState<(PricingCurrencyRow & { countriesText: string })[]>([])
  useEffect(() => {
    if (open) setRows(initial.map((c) => ({ ...c, countriesText: c.countries.join(', ') })))
  }, [open, initial])
  const update = (index: number, patch: Partial<PricingCurrencyRow & { countriesText: string }>) =>
    setRows((list) => list.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  const valid = rows.length > 0 && rows.every((r) => /^[A-Z]{3}$/.test(r.code) && r.symbol.trim())
    && new Set(rows.map((r) => r.code)).size === rows.length
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Currencies</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Visitors from the listed countries see that currency first; everyone else sees the default. A new currency needs a price on every package before you can save.
        </DialogContentText>
        {rows.map((row, index) => (
          <Stack key={index} direction={{ xs: 'column', md: 'row' }} spacing={1.5} mt={2} alignItems={{ md: 'center' }}>
            <TextField label="Code" value={row.code} onChange={(e) => update(index, { code: e.target.value.toUpperCase().slice(0, 3) })} sx={{ width: 100 }} />
            <TextField label="Symbol" value={row.symbol} onChange={(e) => update(index, { symbol: e.target.value })} sx={{ width: 100 }} />
            <TextField label="Tax note" value={row.tax_note} onChange={(e) => update(index, { tax_note: e.target.value })} sx={{ flex: 1 }} />
            <TextField label="Countries" value={row.countriesText} onChange={(e) => update(index, { countriesText: e.target.value })} placeholder="IN, LK" sx={{ width: 150 }} />
            <FormControlLabel
              control={<Radio checked={row.is_default} onChange={() => setRows((list) => list.map((r, i) => ({ ...r, is_default: i === index })))} />}
              label="Default"
            />
            <IconButton aria-label="Remove currency" disabled={rows.length === 1} onClick={() => setRows((list) => list.filter((_, i) => i !== index))}>
              <DeleteForeverRounded fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Button startIcon={<AddRounded />} sx={{ mt: 2 }} onClick={() => setRows((list) => [...list, { code: '', symbol: '', tax_note: '', countries: [], is_default: false, countriesText: '' }])}>
          Add currency
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid}
          onClick={() => onSave(rows.map(({ countriesText, ...row }) => ({
            ...row,
            countries: countriesText.split(/[\s,;]+/).map((c) => c.trim().toUpperCase()).filter(Boolean),
          })))}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function PricingPlans({ overview, notify }: { overview: MasterAdminOverview; notify: (message: string) => void }) {
  const queryClient = useQueryClient()
  const canEdit = useCan()('settings_edit')
  const documentQuery = useQuery({ queryKey: masterAdminQueryKeys.pricingDocument, queryFn: masterAdminApi.pricingDocument })
  const saved = documentQuery.data
  const [draft, setDraft] = useState<PricingDocument | null>(null)
  const [previewCurrency, setPreviewCurrency] = useState<string | null>(null)
  const [editing, setEditing] = useState<{ index: number | null; pkg: PricingPackageRow } | null>(null)
  const [currenciesOpen, setCurrenciesOpen] = useState(false)
  const [reason, setReason] = useState('')
  useEffect(() => { if (saved) setDraft(saved) }, [saved])

  const dirty = Boolean(saved && draft && JSON.stringify(saved) !== JSON.stringify(draft))
  const mutation = useMutation({
    mutationFn: () => masterAdminApi.updatePricing({
      currencies: draft!.currencies,
      packages: draft!.packages,
      annual_discount_percent: draft!.annual_discount_percent,
      reason: reason.trim(),
    }),
    onSuccess: (doc) => {
      queryClient.setQueryData(masterAdminQueryKeys.pricingDocument, doc)
      void queryClient.invalidateQueries({ queryKey: ['master-admin'] })
      setReason('')
      notify('Pricing saved; the website shows it now')
    },
  })

  const doc = draft ?? saved
  const currencies = doc?.currencies ?? []
  const packages = doc?.packages ?? []
  const defaultCurrency = currencies.find((c) => c.is_default)?.code ?? currencies[0]?.code ?? ''
  const preview = currencies.find((c) => c.code === previewCurrency) ?? currencies.find((c) => c.code === defaultCurrency)
  // Display cards need a price or text in every currency; billing cards may stay blank.
  const missingPrices = packages.some((p) => !p.billing_role && currencies.some((c) => !hasAmount(p.prices[c.code]) && !(p.prices[c.code]?.price_label ?? '').trim()))
  const discount = String(doc?.annual_discount_percent ?? '')
  const discountInvalid = !/^\d{1,2}$/.test(discount) || Number(discount) > 90
  const blankBilling = packages
    .filter((p) => p.billing_role)
    .flatMap((p) => currencies.flatMap((c) => planMissing(p, c.code).map((label) => `${p.title} ${c.code} ${label}`)))

  const change = (next: Partial<PricingDocument>) => setDraft((d) => (d ? { ...d, ...next } : d))
  const move = (index: number, delta: number) => {
    const list = [...packages]
    const [item] = list.splice(index, 1)
    list.splice(index + delta, 0, item)
    change({ packages: list })
  }
  const columns = `2fr 1fr ${currencies.map(() => '1fr').join(' ')} .8fr ${canEdit ? '1.2fr' : ''}`.trim()

  return (
    <>
      <PageTitle
        title="Pricing Plans"
        sub={canEdit
          ? 'The packages and prices on the public website. Edit them here, then save; every change is in the Audit Log.'
          : 'The packages and prices on the public website. Only Owners can change them.'}
        action={canEdit && doc && (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => setCurrenciesOpen(true)}>Currencies</Button>
            <Button variant="contained" startIcon={<AddRounded />} onClick={() => setEditing({ index: null, pkg: emptyPackage(currencies) })}>Add package</Button>
          </Stack>
        )}
      />
      <QueryGate isLoading={documentQuery.isLoading} error={documentQuery.error}>
        <Box className="pricingSummary">
          <Paper className="card"><Typography className="tiny">PACKAGES SHOWN</Typography><Typography variant="h5" fontWeight={800}>{packages.filter((p) => p.is_active).length} of {packages.length}</Typography></Paper>
          <Paper className="card"><Typography className="tiny">FREE TRIAL</Typography><Typography variant="h5" fontWeight={800}>{overview.settings.trial_days} days</Typography></Paper>
          <Paper className="card"><Typography className="tiny">DEFAULT CURRENCY</Typography><Typography variant="h5" fontWeight={800}>{defaultCurrency || '—'}</Typography></Paper>
          <Paper className="card">
            <Typography className="tiny">ANNUAL DISCOUNT</Typography>
            {canEdit ? (
              <TextField
                size="small"
                value={discount}
                onChange={(e) => change({ annual_discount_percent: e.target.value.trim() })}
                error={discountInvalid}
                helperText={discountInvalid ? 'A whole number from 0 to 90' : 'Off 12 months, paid up front'}
                inputMode="numeric"
                slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
                sx={{ mt: 0.5, maxWidth: 160 }}
              />
            ) : (
              <Typography variant="h5" fontWeight={800}>{discount}%</Typography>
            )}
          </Paper>
        </Box>

        {blankBilling.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Not set yet, so organizations billed in these currencies can&apos;t subscribe: {blankBilling.join(', ')}.
          </Alert>
        )}
        <Paper className="card tableCard">
          <Box className="table head" style={{ gridTemplateColumns: columns }}>
            <span>PACKAGE</span><span>BILLING</span>
            {currencies.map((c) => <span key={c.code}>{c.code}</span>)}
            <span>STATUS</span>{canEdit && <span>ACTIONS</span>}
          </Box>
          {packages.length === 0 && <Empty>No packages yet.</Empty>}
          {packages.map((pkg, index) => (
            <Box className="table row" key={pkg.key || index} style={{ gridTemplateColumns: columns }}>
              <Box minWidth={0}>
                <Typography fontWeight={700} noWrap>{pkg.title}</Typography>
                {pkg.style === 'featured' && <Typography className="tiny" noWrap>Most popular</Typography>}
              </Box>
              <Box>
                <span>{BILLING_ROLE_LABEL[pkg.billing_role]}</span>
                <Typography className="tiny" noWrap>{pkg.billing_role ? teamSize(pkg) : pkg.user_range_label}</Typography>
              </Box>
              {currencies.map((c) => {
                if (!pkg.billing_role) {
                  return (
                    <Box key={c.code}>
                      <span>{priceText(pkg.prices[c.code], c.symbol)}</span>
                      {hasAmount(pkg.prices[c.code]) && pkg.price_suffix && <Typography className="tiny" noWrap>{pkg.price_suffix}</Typography>}
                    </Box>
                  )
                }
                const price = pkg.prices[c.code]
                const perUser = (value: string | null | undefined) => (filled(value) ? money(c.symbol, value) : '—')
                return (
                  <Box key={c.code} minWidth={0}>
                    <span>{filled(price?.amount) || filled(price?.worker_amount) ? `${perUser(price?.amount)} · ${perUser(price?.worker_amount)}` : 'Not set'}</span>
                    <Typography className="tiny" noWrap>
                      admin · worker{filled(price?.base_amount) ? ` · fee ${money(c.symbol, price?.base_amount)}` : ''}{filled(price?.minimum_amount) ? ` · min ${money(c.symbol, price?.minimum_amount)}` : ''}
                    </Typography>
                  </Box>
                )
              })}
              <Box><Chip size="small" color={pkg.is_active ? 'success' : 'default'} label={pkg.is_active ? 'Shown' : 'Hidden'} /></Box>
              {canEdit && (
                <Stack direction="row" spacing={0.25}>
                  <MuiTooltip title="Edit"><IconButton size="small" onClick={() => setEditing({ index, pkg })}><EditRounded fontSize="small" /></IconButton></MuiTooltip>
                  <MuiTooltip title={pkg.is_active ? 'Hide from the website' : 'Show on the website'}>
                    <IconButton size="small" onClick={() => change({ packages: packages.map((p, i) => (i === index ? { ...p, is_active: !p.is_active } : p)) })}>
                      {pkg.is_active ? <VisibilityOffRounded fontSize="small" /> : <VisibilityRounded fontSize="small" />}
                    </IconButton>
                  </MuiTooltip>
                  <IconButton size="small" aria-label="Move up" disabled={index === 0} onClick={() => move(index, -1)}><ArrowUpwardRounded fontSize="small" /></IconButton>
                  <IconButton size="small" aria-label="Move down" disabled={index === packages.length - 1} onClick={() => move(index, 1)}><ArrowDownwardRounded fontSize="small" /></IconButton>
                  <MuiTooltip title="Remove package">
                    <span><IconButton size="small" color="error" disabled={packages.length === 1} onClick={() => change({ packages: packages.filter((_, i) => i !== index) })}><DeleteForeverRounded fontSize="small" /></IconButton></span>
                  </MuiTooltip>
                </Stack>
              )}
            </Box>
          ))}
        </Paper>

        {canEdit && dirty && (
          <Paper className="card mt">
            <Alert severity={missingPrices || discountInvalid ? 'warning' : 'info'}>
              {missingPrices
                ? 'Some packages have no price in a currency. Edit them before saving.'
                : discountInvalid
                ? 'The annual discount must be a whole number from 0 to 90.'
                : 'You have unsaved pricing changes. Nothing on the website changes until you save.'}
            </Alert>
            <TextField
              label="Reason for the change (saved in the audit log)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              margin="normal"
            />
            {mutation.error && <Alert severity="error" sx={{ mb: 1 }}>{getErrorMessage(mutation.error)}</Alert>}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button disabled={mutation.isPending} onClick={() => { setDraft(saved ?? null); setReason('') }}>Discard</Button>
              <Button
                variant="contained"
                disabled={missingPrices || discountInvalid || reason.trim().length < 5 || mutation.isPending}
                startIcon={mutation.isPending ? <CircularProgress size={16} /> : undefined}
                onClick={() => mutation.mutate()}
              >
                Save pricing
              </Button>
            </Stack>
          </Paper>
        )}

        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={3} mb={1}>
          <Typography fontWeight={800}>Website preview{dirty ? ' (unsaved)' : ''}</Typography>
          <Tabs value={preview?.code ?? false} onChange={(_, v: string) => setPreviewCurrency(v)}>
            {currencies.map((c) => <Tab key={c.code} value={c.code} label={c.code} />)}
          </Tabs>
        </Stack>
        <Box className="planGrid">
          {preview && packages.filter((p) => p.is_active).map((pkg) => (
            <Paper className={`card planCard ${pkg.style === 'featured' ? 'popular' : ''}`} key={pkg.key}>
              {pkg.style === 'featured' && <Chip className="popularBadge" label="Most popular" color="primary" size="small" />}
              <Typography className="tiny">{pkg.user_range_label}</Typography>
              <Typography variant="h6" fontWeight={800} mt={0.5}>{pkg.title}</Typography>
              {pkg.billing_role ? (() => {
                const plan = planSummary(pkg, preview)
                return (
                  <>
                    <Stack direction="row" alignItems="baseline" spacing={0.5} mt={2}>
                      <Typography variant="h4" fontWeight={800}>{plan.headline}</Typography>
                      {plan.suffix && <Typography className="muted">{plan.suffix}</Typography>}
                    </Stack>
                    {plan.lines.map((line) => <Typography key={line} fontSize={13} className="muted">{line}</Typography>)}
                  </>
                )
              })() : (
                <Stack direction="row" alignItems="baseline" spacing={0.5} mt={2}>
                  <Typography variant="h4" fontWeight={800}>{priceText(pkg.prices[preview.code], preview.symbol)}</Typography>
                  {hasAmount(pkg.prices[preview.code]) && <Typography className="muted">{pkg.price_suffix}</Typography>}
                </Stack>
              )}
              <Divider sx={{ my: 2 }} />
              <Typography className="muted">{pkg.description}</Typography>
              {pkg.features.length > 0 && (
                <Box component="ul" sx={{ pl: 2.5, mt: 1.5, mb: 0 }}>
                  {pkg.features.map((f) => <Typography component="li" key={f} fontSize={13} mt={0.5}>{f}</Typography>)}
                </Box>
              )}
            </Paper>
          ))}
        </Box>
        {preview?.tax_note && <Typography className="tiny" mt={1}>{preview.tax_note}</Typography>}
        <Typography className="tiny" mt={1}>
          A customer&apos;s team size picks its plan, and every user pays that plan&apos;s Admin / Manager or Field Worker price
          (after any users the plan fee covers), at least the plan&apos;s minimum, in their country&apos;s currency. Annual billing is {discount || 0}% off. Price changes reach existing subscriptions at
          their next sync.
        </Typography>
      </QueryGate>
      <PackageDialog
        initial={editing?.pkg ?? null}
        isNew={editing?.index === null}
        currencies={currencies}
        existingKeys={packages.map((p) => p.key)}
        onClose={() => setEditing(null)}
        onSave={(pkg) => {
          change({ packages: editing?.index === null ? [...packages, pkg] : packages.map((p, i) => (i === editing?.index ? pkg : p)) })
          setEditing(null)
        }}
      />
      <CurrenciesDialog
        open={currenciesOpen}
        initial={currencies}
        onClose={() => setCurrenciesOpen(false)}
        onSave={(next) => {
          const codes = next.map((c) => c.code)
          change({
            currencies: next,
            packages: packages.map((p) => ({
              ...p,
              prices: Object.fromEntries(codes.map((code) => [code, p.prices[code] ?? { amount: '', price_label: '' }])),
            })),
          })
          setCurrenciesOpen(false)
        }}
      />
    </>
  )
}

const thisMonth = () => new Date().toISOString().slice(0, 7)

const formatUsd = (amount: number) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: amount > 0 && amount < 1 ? 4 : 2 }).format(amount)

function IntegrationCosts({ query }: { query: string }) {
  const [month, setMonth] = useState(thisMonth)
  const costsQuery = useQuery({ queryKey: masterAdminQueryKeys.integrationCosts(month), queryFn: () => masterAdminApi.integrationCosts(month) })
  const report: IntegrationCostReport | undefined = costsQuery.data
  const organizations = (report?.by_organization ?? []).filter((o) => matches(o, query))
  return (
    <>
      <PageTitle
        title="Integration Costs"
        sub="Estimated Twilio, Google Maps and WhatsApp spend per organization. The providers’ own invoices are the final figures."
        action={<TextField type="month" size="small" label="Month" value={month} onChange={(e) => setMonth(e.target.value || thisMonth())} InputLabelProps={{ shrink: true }} />}
      />
      <QueryGate isLoading={costsQuery.isLoading} error={costsQuery.error}>
        {report && (
          <>
            <Box className="metrics">
              <Metric title="ESTIMATED COST" value={formatUsd(report.total_cost)} icon={<ApiRounded />} sub={`${report.calls.toLocaleString()} paid calls`} />
              <Metric title="PAID BY ELLANTEC" value={formatUsd(report.platform_cost)} icon={<CreditCardRounded />} sub="Calls on the platform's own Twilio / Google / WhatsApp accounts" />
              <Metric title="ORGS' OWN ACCOUNTS" value={formatUsd(Math.max(report.total_cost - report.platform_cost, 0))} icon={<BusinessRounded />} sub="Billed to the organization by the provider" />
              <Metric title="ORGANIZATIONS" value={report.by_organization.length} icon={<ApartmentRounded />} sub="With paid calls this month" />
            </Box>

            <Paper className="card tableCard mt">
              <Typography fontWeight={800} sx={{ px: 2.75, pt: 2 }}>By service</Typography>
              <Box className="table costOpTable head">
                {['SERVICE', 'WHAT', 'CALLS', 'UNITS', 'COST', 'PAID BY ELLANTEC'].map((x) => <span key={x}>{x}</span>)}
              </Box>
              {report.by_operation.length === 0 ? <Empty>No paid calls this month.</Empty> : report.by_operation.map((o) => (
                <Box className="table costOpTable row" key={`${o.provider}-${o.operation}`}>
                  <strong>{o.provider_label}</strong>
                  <span>{o.operation_label}</span>
                  <span>{o.calls.toLocaleString()}</span>
                  <span>{o.units.toLocaleString()}</span>
                  <strong>{formatUsd(o.cost)}</strong>
                  <span>{formatUsd(o.platform_cost)}</span>
                </Box>
              ))}
            </Paper>

            <Paper className="card tableCard mt">
              <Typography fontWeight={800} sx={{ px: 2.75, pt: 2 }}>By organization</Typography>
              <Box className="table costOrgTable head">
                {['ORGANIZATION', 'CALLS', 'TWILIO', 'GOOGLE MAPS', 'WHATSAPP', 'TOTAL', 'PAID BY ELLANTEC'].map((x) => <span key={x}>{x}</span>)}
              </Box>
              {organizations.length === 0 ? <Empty>No organizations here.</Empty> : organizations.map((o) => (
                <Box className="table costOrgTable row" key={o.organization_id ?? 'none'}>
                  <strong>{o.organization}</strong>
                  <span>{o.calls.toLocaleString()}</span>
                  <span>{formatUsd(o.twilio_cost)}</span>
                  <span>{formatUsd(o.google_maps_cost)}</span>
                  <span>{o.whatsapp_messages ? `${formatUsd(o.whatsapp_cost)} · ${o.whatsapp_messages.toLocaleString()} msgs` : formatUsd(0)}</span>
                  <strong>{formatUsd(o.cost)}</strong>
                  <span>{formatUsd(o.platform_cost)}</span>
                </Box>
              ))}
            </Paper>
            <Typography className="tiny" sx={{ mt: 1.5 }}>
              Estimates use list prices per SMS segment, verification and Google request (override with INTEGRATION_RATES on the server).
              Map loads count each time the portal opens a map or address search with a Google key; Google bills autocomplete sessions separately. WhatsApp free-form replies (inside Meta’s 24-hour window) cost nothing, so they're counted at $0 unless you set a rate.
            </Typography>
          </>
        )}
      </QueryGate>
    </>
  )
}

type BillingFilter = 'all' | 'paying' | 'trial' | 'attention'

const formatTotals = (totals: CurrencyTotal[]) =>
  totals.length ? totals.map((t) => formatMoney(t.amount, t.currency)).join(' + ') : formatMoney(0, 'GBP')

/** Needs someone to look at it: locked, payment failed, card expiring, or a trial ending with no card. */
const needsAttention = (o: BillingOrganization, failedOrgIds: Set<number>, trialEndingDays: number) =>
  o.is_locked
  || failedOrgIds.has(o.id)
  || Boolean(o.card?.expiring_soon)
  || (o.plan === 'trial' && !o.card && !o.card_exempt && o.trial_days_remaining !== null && o.trial_days_remaining <= trialEndingDays)

function Billing({ query, notify }: { query: string; notify: (m: string) => void }) {
  const canExtend = useCan()('org_status')
  const billingQuery = useQuery({ queryKey: masterAdminQueryKeys.billing, queryFn: masterAdminApi.billing })
  const [filter, setFilter] = useState<BillingFilter>('all')
  const [extending, setExtending] = useState<BillingOrganization | null>(null)
  const data = billingQuery.data
  const failedOrgIds = new Set((data?.failed_payments ?? []).map((f) => f.organization_id).filter((id): id is number => id !== null))
  const rows = (data?.organizations ?? [])
    .filter((o) => matches(o, query))
    .filter((o) => filter === 'all'
      || (filter === 'paying' && o.plan === 'active')
      || (filter === 'trial' && o.plan === 'trial')
      || (filter === 'attention' && needsAttention(o, failedOrgIds, data?.trial_ending_days ?? 7)))
  const attentionCount = (data?.organizations ?? []).filter((o) => needsAttention(o, failedOrgIds, data?.trial_ending_days ?? 7)).length

  return (
    <>
      <PageTitle
        title="Billing"
        sub="Revenue, cards on file and trials across every customer organization."
        action={data?.stripe_mode === 'test' ? <Chip color="warning" label="Stripe test mode" /> : undefined}
      />
      <QueryGate isLoading={billingQuery.isLoading} error={billingQuery.error}>
        {data && (
          <>
            {!data.configured && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Stripe isn't set up in this environment (STRIPE_PLATFORM_SECRET_KEY is empty), so there are no cards, charges or failed payments yet.
              </Alert>
            )}
            {data.stripe_error && <Alert severity="warning" sx={{ mt: 2 }}>{data.stripe_error}</Alert>}
            {data.pricing_gaps.map((gap) => (
              <Alert severity="error" sx={{ mt: 2 }} key={gap.currency || 'none'}>
                {gap.currency || 'No currency'}: {gap.organizations} organization{gap.organizations === 1 ? '' : 's'} can&apos;t be
                billed until these are set under Pricing Plans: {gap.missing.join(', ')}.
              </Alert>
            ))}
            <Box className="metrics">
              <Metric title="MONTHLY REVENUE" value={formatTotals(data.mrr)} icon={<CreditCardRounded />} sub={`${data.counts.paying} paying organization${data.counts.paying === 1 ? '' : 's'}`} />
              <Metric title="TRIAL PIPELINE" value={formatTotals(data.trial_pipeline)} icon={<GroupsRounded />} sub={`Per month once trials end · ${data.counts.trials_with_card} of ${data.counts.trials} trials have a card`} />
              <Metric title="TRIALS ENDING" value={data.counts.trials_ending_soon} icon={<MoreTimeRounded />} sub={`Next ${data.trial_ending_days} days · ${data.counts.trials_ending_without_card} without a card`} />
              <Metric title="FAILED PAYMENTS" value={data.counts.failed_payments} icon={<ErrorOutlineRounded />} sub={`${data.counts.cards_expiring} card${data.counts.cards_expiring === 1 ? '' : 's'} expiring soon · ${data.counts.locked} locked`} />
            </Box>

            {data.failed_payments.length > 0 && (
              <Paper className="card tableCard mt">
                <Typography fontWeight={800} sx={{ px: 2.75, pt: 2 }}>Failed payments</Typography>
                <Box className="table failedTable head">
                  {['INVOICE', 'ORGANIZATION', 'AMOUNT', 'ATTEMPTS', 'NEXT RETRY', ''].map((x, i) => <span key={`${x}-${i}`}>{x}</span>)}
                </Box>
                {data.failed_payments.map((f) => (
                  <Box className="table failedTable row" key={f.id}>
                    <strong>{f.number}</strong>
                    <span>{f.organization || '—'}</span>
                    <strong>{formatMoney(f.amount_due, f.currency)}</strong>
                    <span>{f.attempt_count}</span>
                    <span>{f.next_payment_attempt ? formatDate(f.next_payment_attempt) : 'No more retries'}</span>
                    <Stack direction="row" spacing={0.5}>
                      {f.stripe_url && (
                        <MuiTooltip title="Open in Stripe">
                          <IconButton size="small" href={f.stripe_url} target="_blank" rel="noopener noreferrer"><OpenInNewRounded fontSize="small" /></IconButton>
                        </MuiTooltip>
                      )}
                    </Stack>
                  </Box>
                ))}
              </Paper>
            )}

            <Paper className="card tableCard mt">
              <Tabs value={filter} onChange={(_, v: BillingFilter) => setFilter(v)} sx={{ px: 2 }}>
                <Tab value="all" label="All" />
                <Tab value="paying" label={`Paying (${data.counts.paying})`} />
                <Tab value="trial" label={`Trials (${data.counts.trials})`} />
                <Tab value="attention" label={`Needs attention (${attentionCount})`} />
              </Tabs>
              <Divider />
              <Box className="table billingTable head">
                {['ORGANIZATION', 'PLAN', 'CARD', 'BILLING', 'TRIAL ENDS', ''].map((x, i) => <span key={`${x}-${i}`}>{x}</span>)}
              </Box>
              {rows.length === 0 ? <Empty>No organizations here.</Empty> : rows.map((o) => {
                const meta = PLAN_META[o.plan] ?? PLAN_META.trial
                return (
                  <Box className="table billingTable row" key={o.id}>
                    <Box>
                      <strong>{o.name}</strong>
                      <Typography className="tiny">
                        {o.country || '—'} · {o.admin_count ?? o.user_count} admin · {o.worker_count ?? 0} field worker
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      <Chip size="small" color={meta.color} label={meta.label} />
                      {failedOrgIds.has(o.id) && <Chip size="small" color="error" variant="outlined" label="Payment failed" />}
                      {o.card_exempt && <MuiTooltip title="Signs up without a card (card-exempt country)"><Chip size="small" variant="outlined" label="No card needed" /></MuiTooltip>}
                    </Stack>
                    {o.card ? (
                      <Box>
                        <span style={{ textTransform: 'capitalize' }}>{o.card.brand} ••{o.card.last4}</span>
                        <Typography className="tiny" color={o.card.expiring_soon ? 'error' : undefined}>
                          {o.card.exp_month && o.card.exp_year ? `Expires ${String(o.card.exp_month).padStart(2, '0')}/${String(o.card.exp_year).slice(-2)}` : ''}
                          {o.card.expiring_soon ? ' · expiring soon' : ''}
                        </Typography>
                      </Box>
                    ) : <span className="tiny">No card</span>}
                    {o.total_amount !== null && o.interval ? (
                      <Box>
                        <strong>{formatMoney(o.total_amount, o.currency)}/{o.interval === 'year' ? 'yr' : 'mo'}</strong>
                        <Typography className="tiny">
                          {o.billed_admins} admin + {o.billed_workers} field worker{o.interval === 'year' ? ' · annual' : ''}
                        </Typography>
                      </Box>
                    ) : <span>—</span>}
                    <Box>
                      <span>{formatDate(o.trial_end_on)}</span>
                      {o.plan === 'trial' && o.trial_days_remaining !== null && (
                        <Typography className="tiny" color={o.trial_days_remaining <= data.trial_ending_days ? 'warning.main' : undefined}>
                          {o.trial_days_remaining} day{o.trial_days_remaining === 1 ? '' : 's'} left
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {canExtend && o.plan !== 'active' && (
                        <MuiTooltip title="Extend trial">
                          <IconButton size="small" onClick={() => setExtending(o)}><MoreTimeRounded fontSize="small" /></IconButton>
                        </MuiTooltip>
                      )}
                      {o.stripe_customer_url && (
                        <MuiTooltip title="Open customer in Stripe">
                          <IconButton size="small" href={o.stripe_customer_url} target="_blank" rel="noopener noreferrer"><OpenInNewRounded fontSize="small" /></IconButton>
                        </MuiTooltip>
                      )}
                    </Stack>
                  </Box>
                )
              })}
            </Paper>
          </>
        )}
      </QueryGate>
      <ExtendTrialDialog org={extending} onClose={() => setExtending(null)} onDone={(m) => { setExtending(null); notify(m) }} />
    </>
  )
}

const EXTEND_PRESETS = [7, 14, 30]

function ExtendTrialDialog({ org, onClose, onDone }: {
  org: BillingOrganization | null
  onClose: () => void
  onDone: (message: string) => void
}) {
  const queryClient = useQueryClient()
  const [days, setDays] = useState('14')
  const [reason, setReason] = useState('')
  useEffect(() => { setDays('14'); setReason('') }, [org?.id])

  const mutation = useMutation({
    mutationFn: () => masterAdminApi.extendTrial(org!.id, { days: Number(days), reason: reason.trim() }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['master-admin'] })
      onDone(`${org!.name}: trial extended by ${days} days.${result.stripe_updated ? ' The Stripe trial moved too.' : ''}`)
    },
  })

  if (!org) return null
  const dayCount = Number(days)
  const validDays = Number.isInteger(dayCount) && dayCount >= 1 && dayCount <= 90
  // Same rule as the server: a running trial is extended from its end, an ended one from today.
  const currentEnd = org.trial_end_on ? new Date(org.trial_end_on) : null
  const base = currentEnd && currentEnd > new Date() ? currentEnd : new Date()
  const newEnd = validDays ? new Date(base.getTime() + dayCount * 86400000) : null

  return (
    <Dialog open onClose={mutation.isPending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Extend trial: {org.name}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {org.plan === 'trial'
            ? `The trial currently ends ${formatDate(org.trial_end_on)}.`
            : 'The trial has ended, so the extra days start from today and the organization is unlocked straight away.'}
          {org.card ? ' Its card stays on file and is first charged when the new trial ends.' : ''}
        </DialogContentText>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          {EXTEND_PRESETS.map((d) => (
            <Chip key={d} label={`${d} days`} color={days === String(d) ? 'primary' : 'default'} onClick={() => setDays(String(d))} />
          ))}
        </Stack>
        <TextField
          label="Days"
          type="number"
          value={days}
          onChange={(e) => setDays(e.target.value)}
          margin="normal"
          inputProps={{ min: 1, max: 90 }}
          helperText={newEnd ? `New trial end: ${formatDate(newEnd.toISOString())}` : 'Between 1 and 90 days.'}
          error={!validDays}
        />
        <TextField
          label="Reason (saved in the audit log)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          margin="dense"
          helperText="At least 5 characters."
        />
        {mutation.error && <Alert severity="error" sx={{ mt: 1 }}>{getErrorMessage(mutation.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={mutation.isPending ? <CircularProgress size={16} /> : <MoreTimeRounded />}
          disabled={!validDays || reason.trim().length < 5 || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          Extend trial
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function Usage({ overview }: { overview: MasterAdminOverview }) {
  const orgs = [...overview.organizations].sort((a, b) => b.billing.user_count - a.billing.user_count)
  // Bars are relative to the largest organization.
  const largest = Math.max(1, ...orgs.map((o) => o.billing.user_count))
  const workers = orgs.reduce((sum, o) => sum + (o.billing.worker_count ?? 0), 0)
  return (
    <>
      <PageTitle title="Usage & Health" sub="Adoption per organization: licences and branches." />
      <Box className="metrics">
        <Metric title="TOTAL USERS" value={overview.totals.users} icon={<GroupsRounded />} sub="Licensed staff" />
        <Metric title="BRANCHES" value={overview.totals.branches} icon={<ApartmentRounded />} sub="Across all tenants" />
        <Metric title="FIELD WORKERS" value={workers} icon={<HealthAndSafetyRounded />} sub="Mobile-app-only licences" />
        <Metric title="LOCKED" value={overview.totals.plans.locked} icon={<SupportAgentRounded />} sub="Need a subscription to continue" />
      </Box>
      <Paper className="card mt">
        {orgs.length === 0 ? <Empty>No customer organizations found.</Empty> : orgs.map((o) => (
          <Box key={o.id} className="healthRow">
            <Box>
              <Typography fontWeight={700}>{o.name}</Typography>
              <Typography className="tiny">
                {o.billing.user_count} users
                {o.billing.admin_count !== null ? ` (${o.billing.admin_count} admin, ${o.billing.worker_count} field worker)` : ''}
                {' • '}{o.branch_count} branches
              </Typography>
            </Box>
            <Box flex={1}>
              <LinearProgress
                variant="determinate"
                color={o.billing.is_locked ? 'error' : 'primary'}
                value={Math.min(100, (o.billing.user_count / largest) * 100)}
                sx={{ height: 8, borderRadius: 5 }}
              />
            </Box>
            <PlanChip org={o} />
          </Box>
        ))}
      </Paper>
    </>
  )
}

type DiskVolume = { path: string; used_percent: number; free_gb: number }

function CheckDetails({ check }: { check: HealthCheck }) {
  if (check.name === 'disk') {
    const volumes = (check.data.volumes ?? {}) as Record<string, DiskVolume>
    const warnAt = Number(check.data.warn_percent ?? 85)
    const failAt = Number(check.data.critical_percent ?? 95)
    return (
      <Box mt={1.5}>
        {Object.entries(volumes).map(([label, volume]) => (
          <Box key={label} mb={1.2}>
            <Stack direction="row" justifyContent="space-between">
              <Typography fontSize={12.5} fontWeight={700}>{label} <Typography component="span" className="tiny">{volume.path}</Typography></Typography>
              <Typography fontSize={12.5}>{volume.used_percent}% used · {volume.free_gb} GB free</Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, volume.used_percent)}
              color={volume.used_percent >= failAt ? 'error' : volume.used_percent >= warnAt ? 'warning' : 'primary'}
              sx={{ mt: 0.7, height: 7, borderRadius: 5 }}
            />
          </Box>
        ))}
        <Typography className="tiny">Warning at {warnAt}% · critical at {failAt}%</Typography>
      </Box>
    )
  }
  if (check.name === 'memory' && check.data.used_percent != null) {
    const used = Number(check.data.used_percent)
    const warnAt = Number(check.data.warn_percent ?? 90)
    return (
      <Box mt={1.5}>
        <Stack direction="row" justifyContent="space-between">
          <Typography fontSize={12.5} fontWeight={700}>{used}% used</Typography>
          <Typography fontSize={12.5}>{String(check.data.available_gb)} GB free of {String(check.data.total_gb)} GB</Typography>
        </Stack>
        <LinearProgress variant="determinate" value={Math.min(100, used)} color={used >= warnAt ? 'warning' : 'primary'} sx={{ mt: 0.7, height: 7, borderRadius: 5 }} />
        <Typography className="tiny" mt={0.7}>
          Warning at {warnAt}%{check.data.swap_used_percent != null ? ` · swap ${String(check.data.swap_used_percent)}% used` : ''}
        </Typography>
      </Box>
    )
  }
  if (check.name === 'cpu' && check.data.load_per_core_5m != null) {
    const perCore = Number(check.data.load_per_core_5m)
    const warnAt = Number(check.data.warn_per_core ?? 1.5)
    return (
      <Box mt={1.5}>
        <Stack direction="row" justifyContent="space-between">
          <Typography fontSize={12.5} fontWeight={700}>{perCore} per core</Typography>
          <Typography fontSize={12.5}>{String(check.data.cores)} cores</Typography>
        </Stack>
        <LinearProgress variant="determinate" value={Math.min(100, (perCore / warnAt) * 100)} color={perCore >= warnAt ? 'warning' : 'primary'} sx={{ mt: 0.7, height: 7, borderRadius: 5 }} />
        <Typography className="tiny" mt={0.7}>
          Load 1m {String(check.data.load_1m)} · 5m {String(check.data.load_5m)} · 15m {String(check.data.load_15m)} · warning at {warnAt} per core
        </Typography>
      </Box>
    )
  }
  if (check.name === 'migrations' && Array.isArray(check.data.pending) && check.data.pending.length > 0) {
    return (
      <Box mt={1.5} className="codeList">
        {(check.data.pending as string[]).map((name) => <code key={name}>{name}</code>)}
      </Box>
    )
  }
  if (check.name === 'celery_heartbeat' && check.data.max_age_seconds) {
    return <Typography className="tiny" mt={1}>Alerts when the last heartbeat is older than {String(check.data.max_age_seconds)}s</Typography>
  }
  return null
}

function SystemHealth({ onRecheck }: { onRecheck: () => void }) {
  const healthQuery = useQuery({
    queryKey: masterAdminQueryKeys.systemHealth,
    queryFn: masterAdminApi.systemHealth,
    refetchInterval: 30_000,
  })
  const report = healthQuery.data
  const passing = report?.checks.filter((c) => c.status === 'ok').length ?? 0
  const failing = report?.checks.filter((c) => c.status === 'fail' || c.status === 'warn') ?? []
  const database = report?.checks.find((c) => c.name === 'database')

  return (
    <>
      <PageTitle
        title="System Health"
        sub="Live status of the production API and the services it depends on. Refreshes every 30 seconds."
        action={(
          <Button variant="outlined" startIcon={healthQuery.isFetching ? <CircularProgress size={16} /> : <RefreshRounded />} onClick={onRecheck} disabled={healthQuery.isFetching}>
            Check now
          </Button>
        )}
      />
      <QueryGate isLoading={healthQuery.isLoading} error={healthQuery.error}>
        {report && (
          <>
            <Paper className={`card healthBanner ${report.status}`}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box className="healthDot" />
                <Box flex={1}>
                  <Typography variant="h6" fontWeight={800}>{SYSTEM_STATUS[report.status].banner}</Typography>
                  <Typography className="muted">
                    {failing.length === 0
                      ? `All ${report.checks.length} checks passing.`
                      : report.summary && report.summary !== 'ok'
                        ? report.summary
                        : `Needs attention: ${failing.map((c) => CHECK_LABELS[c.name]?.title ?? c.name).join(', ')}.`}
                  </Typography>
                </Box>
                <Box textAlign="right">
                  <Typography className="tiny">LAST CHECKED</Typography>
                  <Typography fontWeight={700}>{formatTime(report.checked_at)}</Typography>
                </Box>
              </Stack>
            </Paper>
            <Box className="metrics">
              <Metric title="CHECKS PASSING" value={`${passing} / ${report.checks.length}`} icon={<MonitorHeartRounded />} sub="Warnings and failures listed below" />
              <Metric title="DATABASE LATENCY" value={database?.latency_ms != null ? `${database.latency_ms} ms` : '—'} icon={<AnalyticsRounded />} sub="Round-trip for SELECT 1" />
              <Metric title="SERVER" value={<Typography component="span" variant="h6" fontWeight={800} noWrap>{report.host}</Typography>} icon={<SettingsRounded />} sub="Host that answered this check" />
              <Metric title="RELEASE" value={report.version || '—'} icon={<ReceiptLongRounded />} sub="APP_RELEASE on the server" />
            </Box>
            <Box className="healthGrid">
              {report.checks.map((check) => {
                const meta = CHECK_LABELS[check.name] ?? { title: check.name, description: '' }
                return (
                  <Paper key={check.name} className={`card healthCheck ${check.status}`}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box>
                        <Typography fontWeight={800}>{meta.title}</Typography>
                        <Typography className="tiny">{meta.description}</Typography>
                      </Box>
                      <Chip size="small" color={CHECK_STATUS[check.status]?.color} label={CHECK_STATUS[check.status]?.label ?? check.status} />
                    </Stack>
                    <Typography fontSize={13} mt={1.5}>{check.detail || '—'}</Typography>
                    <CheckDetails check={check} />
                    <Stack direction="row" spacing={1} mt={1.5}>
                      {check.critical && <Chip size="small" variant="outlined" label="Critical" />}
                      {check.latency_ms != null && <Chip size="small" variant="outlined" label={`${check.latency_ms} ms`} />}
                    </Stack>
                  </Paper>
                )
              })}
            </Box>
            <Paper className="card mt">
              <Typography fontWeight={800}>How alerts work</Typography>
              <Typography className="muted" mt={1}>
                A failing <strong>critical</strong> check takes the platform <strong>down</strong> (<code>/health/ready/</code> answers 503).
                Other failures and warnings mark it <strong>degraded</strong> while the API keeps serving requests.
                This page only shows status while someone has it open. Slack <code>#monitoring</code> alerts come from Uptime Kuma, which posts the line in the banner above
                as the cause, and separately alerts when the server is unreachable or its SSL certificate is invalid or about to expire.
              </Typography>
            </Paper>
          </>
        )}
      </QueryGate>
    </>
  )
}

function Reports({ overview }: { overview: MasterAdminOverview }) {
  const { totals } = overview
  const conversion = totals.organizations ? Math.round(((totals.plans.active ?? 0) / totals.organizations) * 100) : 0
  const byVertical = Object.entries(
    overview.organizations.reduce<Record<string, number>>((acc, o) => {
      const key = shortVertical(o.vertical)
      acc[key] = (acc[key] ?? 0) + 1
      return acc
    }, {}),
  )
  return (
    <>
      <PageTitle title="Reports" sub="A management summary of customer and service performance." />
      <Box className="metrics">
        <Metric title="PAID CONVERSION" value={`${conversion}%`} icon={<AnalyticsRounded />} sub="Organizations with a paid subscription" />
        <Metric title="SIGNUPS (6 MONTHS)" value={overview.signups_by_month.reduce((a, m) => a + m.count, 0)} icon={<BusinessRounded />} sub="New customer organizations" />
        <Metric title="OPEN REQUESTS" value={totals.open_support_requests} icon={<SupportAgentRounded />} sub="Support workload" />
        <Metric title="AVG USERS / TENANT" value={Math.round(totals.users / Math.max(totals.organizations, 1))} icon={<GroupsRounded />} sub="Adoption indicator" />
      </Box>
      <Paper className="card mt">
        <Typography fontWeight={800}>Organizations by vertical</Typography>
        {byVertical.length === 0 ? <Empty>No customer organizations yet.</Empty> : byVertical.map(([name, count]) => <Info key={name} k={name} v={count} />)}
      </Paper>
    </>
  )
}

type InviteCredentials = NonNullable<StaffInviteResult['credentials']>

function InviteDialog({ open, roles, onClose, onDone }: {
  open: boolean
  roles: { value: MasterAdminRole; label: string; description: string }[]
  onClose: () => void
  onDone: (result: StaffInviteResult) => void
}) {
  const empty = { first_name: '', last_name: '', email: '', role: 'support' as MasterAdminRole }
  const [form, setForm] = useState(empty)
  useEffect(() => { if (open) setForm(empty) }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  const mutation = useMutation({
    mutationFn: () => masterAdminApi.inviteStaff({ ...form, email: form.email.trim(), first_name: form.first_name.trim(), last_name: form.last_name.trim() }),
    onSuccess: onDone,
  })
  const set = (name: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [name]: e.target.value }))
  const canSubmit = form.first_name.trim() && /^\S+@\S+\.\S+$/.test(form.email.trim())
  return (
    <Dialog open={open} onClose={mutation.isPending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Invite a team member</DialogTitle>
      <DialogContent>
        <DialogContentText>
          They get an email with a temporary password and must choose their own the first time they sign in.
        </DialogContentText>
        <Stack direction="row" spacing={2} mt={1}>
          <TextField label="First name" value={form.first_name} onChange={set('first_name')} fullWidth margin="dense" autoFocus />
          <TextField label="Last name" value={form.last_name} onChange={set('last_name')} fullWidth margin="dense" />
        </Stack>
        <TextField label="Email" type="email" value={form.email} onChange={set('email')} fullWidth margin="dense" />
        <TextField
          select
          label="Role"
          value={form.role}
          onChange={set('role')}
          fullWidth
          margin="dense"
          helperText={roles.find((r) => r.value === form.role)?.description}
        >
          {roles.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
        </TextField>
        {mutation.error && <Alert severity="error" sx={{ mt: 1 }}>{getErrorMessage(mutation.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={mutation.isPending ? <CircularProgress size={16} /> : <PersonAddRounded />}
          disabled={!canSubmit || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          Send invite
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function InviteCredentialsDialog({ credentials, onClose, notify }: {
  credentials: InviteCredentials | null
  onClose: () => void
  notify: (message: string) => void
}) {
  if (!credentials) return null
  const copy = (value: string, what: string) => {
    void navigator.clipboard?.writeText(value).then(() => notify(`${what} copied`), () => notify(`Copy failed; select the ${what.toLowerCase()} and copy it by hand`))
  }
  const rows: [string, string][] = [
    ['Business code', credentials.business_code],
    ['Username', credentials.username],
    ['Temporary password', credentials.password],
  ]
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Share these sign-in details</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          The invitation email couldn't be sent. Pass these on securely; the password is shown only once and must be changed at first sign-in before {formatTime(credentials.expires_at)}.
        </Alert>
        {rows.map(([label, value]) => (
          <Stack key={label} direction="row" alignItems="center" justifyContent="space-between" className="info">
            <Typography className="muted">{label}</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography fontWeight={700} sx={{ fontFamily: label === 'Temporary password' ? 'monospace' : undefined }}>{value}</Typography>
              <IconButton size="small" onClick={() => copy(value, label)}><ContentCopyRounded fontSize="small" /></IconButton>
            </Stack>
          </Stack>
        ))}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  )
}

function memberStatus(m: MasterAdminStaffMember): { label: string; color: ChipProps['color'] } {
  if (!m.role) return { label: 'No access', color: 'default' }
  if (!m.active) return { label: 'Disabled', color: 'error' }
  if (m.invite_pending) return { label: 'Invite pending', color: 'warning' }
  return { label: 'Active', color: 'success' }
}

function TeamPage({ query, notify }: { query: string; notify: (message: string) => void }) {
  const queryClient = useQueryClient()
  const teamQuery = useQuery({ queryKey: masterAdminQueryKeys.team, queryFn: masterAdminApi.team })
  const [inviteOpen, setInviteOpen] = useState(false)
  const [removing, setRemoving] = useState<MasterAdminStaffMember | null>(null)
  const [credentials, setCredentials] = useState<InviteCredentials | null>(null)
  const invalidate = () => { void queryClient.invalidateQueries({ queryKey: ['master-admin'] }) }
  const showResult = (result: StaffInviteResult, sentMessage: string) => {
    invalidate()
    if (result.email_sent) {
      notify(sentMessage)
    } else {
      setCredentials(result.credentials ?? null)
    }
  }
  const onError = (error: unknown) => notify(getErrorMessage(error))
  const roleMutation = useMutation({
    mutationFn: ({ postId, role }: { postId: number; role: MasterAdminRole }) => masterAdminApi.changeStaffRole(postId, role),
    onSuccess: (member) => { invalidate(); notify(`${member.name} is now ${member.role_label}`) },
    onError,
  })
  const resendMutation = useMutation({
    mutationFn: (postId: number) => masterAdminApi.resendStaffInvite(postId),
    onSuccess: (result) => showResult(result, `New invitation sent to ${result.member.email}`),
    onError,
  })
  const removeMutation = useMutation({
    mutationFn: (postId: number) => masterAdminApi.removeStaff(postId),
    onSuccess: () => { notify(`${removing?.name ?? 'Team member'} removed`); setRemoving(null); invalidate() },
    onError,
  })

  const team = teamQuery.data
  const members = (team?.members ?? []).filter((m) => matches(m, query))
  const busy = roleMutation.isPending || resendMutation.isPending
  return (
    <>
      <PageTitle
        title="Team"
        sub="Who can sign in to the master admin and what they can do. Every change is in the Audit Log."
        action={<Button variant="contained" startIcon={<PersonAddRounded />} onClick={() => setInviteOpen(true)} disabled={!team}>Invite</Button>}
      />
      <QueryGate isLoading={teamQuery.isLoading} error={teamQuery.error}>
        <Paper className="card tableCard">
          <Box className="table teamTable head"><span>MEMBER</span><span>ROLE</span><span>STATUS</span><span>ADDED</span><span>ACTIONS</span></Box>
          {members.length === 0 && <Empty>No team members match.</Empty>}
          {members.map((m) => {
            const locked = m.is_owner_account || m.is_you
            const status = memberStatus(m)
            return (
              <Box className="table teamTable row" key={m.post_id}>
                <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                  <Avatar>{initials(m.name || m.email)}</Avatar>
                  <Box minWidth={0}>
                    <Typography fontWeight={700} noWrap>{m.name || '—'}{m.is_you ? ' (you)' : ''}</Typography>
                    <Typography className="tiny" noWrap>{m.email}</Typography>
                  </Box>
                </Stack>
                {locked ? (
                  <MuiTooltip title={m.is_owner_account ? 'The root owner account is always Owner.' : "You can't change your own role."}>
                    <Box><Chip size="small" label={m.role_label} /></Box>
                  </MuiTooltip>
                ) : (
                  <TextField
                    select
                    size="small"
                    value={m.role ?? ''}
                    disabled={busy}
                    onChange={(e) => roleMutation.mutate({ postId: m.post_id, role: e.target.value as MasterAdminRole })}
                    SelectProps={{ displayEmpty: true }}
                  >
                    {!m.role && <MenuItem value="" disabled>No access</MenuItem>}
                    {team?.roles.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                  </TextField>
                )}
                <Box><Chip size="small" color={status.color} label={status.label} /></Box>
                <Box>
                  <span>{formatDate(m.added_on)}</span>
                  {m.invited_by && <Typography className="tiny">by {m.invited_by}</Typography>}
                </Box>
                <Stack direction="row" spacing={0.5}>
                  {!locked && m.role && (
                    <MuiTooltip title="Email a new temporary password">
                      <span><IconButton size="small" disabled={busy} onClick={() => resendMutation.mutate(m.post_id)}><ForwardToInboxRounded fontSize="small" /></IconButton></span>
                    </MuiTooltip>
                  )}
                  {!locked && (
                    <MuiTooltip title="Remove from the team">
                      <IconButton size="small" color="error" onClick={() => setRemoving(m)}><PersonRemoveRounded fontSize="small" /></IconButton>
                    </MuiTooltip>
                  )}
                </Stack>
              </Box>
            )
          })}
        </Paper>
        {team && (
          <Box className="roleGrid mt">
            {team.roles.map((r) => (
              <Paper className="card" key={r.value}>
                <Typography fontWeight={800}>{r.label}</Typography>
                <Typography className="muted" mt={0.5}>{r.description}</Typography>
              </Paper>
            ))}
          </Box>
        )}
        {team && (
          <Typography className="tiny" mt={1.5}>
            Temporary passwords must be changed within {team.invite_password_days} days. If someone misses that, send them a new invite.
          </Typography>
        )}
      </QueryGate>
      <InviteDialog
        open={inviteOpen}
        roles={team?.roles ?? []}
        onClose={() => setInviteOpen(false)}
        onDone={(result) => { setInviteOpen(false); showResult(result, `Invitation sent to ${result.member.email}`) }}
      />
      <InviteCredentialsDialog credentials={credentials} onClose={() => setCredentials(null)} notify={notify} />
      <Dialog open={!!removing} onClose={removeMutation.isPending ? undefined : () => setRemoving(null)} fullWidth maxWidth="xs">
        <DialogTitle>Remove {removing?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            They lose master admin access straight away and their sign-in is disabled. You can invite them again later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)} disabled={removeMutation.isPending}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={removeMutation.isPending}
            startIcon={removeMutation.isPending ? <CircularProgress size={16} /> : <PersonRemoveRounded />}
            onClick={() => removing && removeMutation.mutate(removing.post_id)}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const SETTING_LABELS: Record<string, string> = {
  trial_days: 'Free trial length (days)',
  signup_alert_emails: 'Signup alert emails',
  support_alert_emails: 'Support alert emails',
}

const parseEmails = (text: string) => text.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean)

function SettingsForm({ settings, notify }: {
  settings: PlatformSettings
  notify: (message: string) => void
}) {
  const queryClient = useQueryClient()
  const canEdit = useCan()('settings_edit')
  const initial = useMemo(() => ({
    trial_days: String(settings.trial_days),
    signup_alert_emails: settings.signup_alert_emails.join(', '),
    support_alert_emails: settings.support_alert_emails.join(', '),
  }), [settings])
  const [form, setForm] = useState(initial)
  const [reason, setReason] = useState('')
  useEffect(() => { setForm(initial) }, [initial])

  const set = (name: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [name]: e.target.value }))
  const changed = (Object.keys(form) as (keyof typeof form)[]).filter((name) => (
    name.endsWith('_emails')
      ? parseEmails(form[name]).join(',').toLowerCase() !== parseEmails(initial[name]).join(',').toLowerCase()
      : form[name].trim() !== initial[name]
  ))

  const mutation = useMutation({
    mutationFn: () => masterAdminApi.updateSettings({
      reason: reason.trim(),
      ...(changed.includes('trial_days') ? { trial_days: Number(form.trial_days) } : {}),
      ...(changed.includes('signup_alert_emails') ? { signup_alert_emails: parseEmails(form.signup_alert_emails) } : {}),
      ...(changed.includes('support_alert_emails') ? { support_alert_emails: parseEmails(form.support_alert_emails) } : {}),
    }),
    onSuccess: () => {
      setReason('')
      notify('Platform settings saved')
      void queryClient.invalidateQueries({ queryKey: ['master-admin'] })
    },
  })

  const { limits } = settings
  return (
    <Paper className="card settings">
      <Typography fontWeight={800}>Signup and billing</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={2}>
        <TextField
          label={SETTING_LABELS.trial_days}
          type="number"
          value={form.trial_days}
          onChange={set('trial_days')}
          disabled={!canEdit}
          inputProps={{ min: limits.trial_days.min, max: limits.trial_days.max }}
          helperText="Applies to new signups. Existing trials keep their end date."
          fullWidth
        />
      </Stack>
      <Alert severity="info" sx={{ mt: 2 }}>
        Licence prices and the minimum subscription are set per currency under Pricing Plans.
      </Alert>
      <Divider sx={{ my: 3 }} />
      <Typography fontWeight={800}>Alert emails</Typography>
      <TextField
        label={SETTING_LABELS.signup_alert_emails}
        value={form.signup_alert_emails}
        onChange={set('signup_alert_emails')}
        disabled={!canEdit}
        helperText="Told about every new customer signup. Separate addresses with commas. Leave empty to turn off."
        fullWidth
        multiline
        margin="normal"
      />
      <TextField
        label={SETTING_LABELS.support_alert_emails}
        value={form.support_alert_emails}
        onChange={set('support_alert_emails')}
        disabled={!canEdit}
        helperText="Told about new support requests and customer replies. Leave empty to turn off."
        fullWidth
        multiline
        margin="normal"
      />
      <Divider sx={{ my: 3 }} />
      {!canEdit ? <Alert severity="info">Only Owners can change platform settings.</Alert> : <>
      <TextField
        label="Reason for the change (saved in the audit log)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        fullWidth
        disabled={changed.length === 0}
        helperText={changed.length ? `Changing: ${changed.map((c) => SETTING_LABELS[c]).join(', ')}` : 'Edit a setting above to save.'}
      />
      {mutation.error && <Alert severity="error" sx={{ mt: 2 }}>{getErrorMessage(mutation.error)}</Alert>}
      <Stack direction="row" spacing={1} justifyContent="flex-end" mt={2}>
        <Button disabled={changed.length === 0 || mutation.isPending} onClick={() => { setForm(initial); setReason('') }}>Discard</Button>
        <Button
          variant="contained"
          disabled={changed.length === 0 || reason.trim().length < 5 || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} /> : undefined}
          onClick={() => mutation.mutate()}
        >
          Save changes
        </Button>
      </Stack>
      </>}
    </Paper>
  )
}

function Settings({ overview, notify }: { overview: MasterAdminOverview; notify: (message: string) => void }) {
  const { settings } = overview
  return (
    <>
      <PageTitle
        title="Platform Settings"
        sub={settings.customized
          ? `Last changed ${formatTime(settings.updated_at)}${settings.updated_by ? ` by ${settings.updated_by}` : ''}. Every change is in the Audit Log.`
          : 'Showing the environment defaults; saving here overrides them. Every change is in the Audit Log.'}
      />
      <SettingsForm settings={settings} notify={notify} />
      <Paper className="card settings mt">
        <Typography fontWeight={800}>Read-only</Typography>
        <Info k="Root organization" v={overview.root_org.name} />
        <Info k="Platform Stripe billing" v={settings.stripe_configured ? 'Configured' : 'Not configured (STRIPE_PLATFORM_SECRET_KEY)'} />
        <Typography className="tiny" mt={1}>Secrets such as Stripe keys stay in the server environment and can't be edited here.</Typography>
      </Paper>
    </>
  )
}

export default function MasterAdminPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useAuthStore((state) => state.user)
  const currentPost = useAuthStore((state) => state.currentPost)
  const [page, setPage] = useState<PageName>('Dashboard')
  const [query, setQuery] = useState('')
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null)
  const [parentFilter, setParentFilter] = useState('All')
  const [activeRequestId, setActiveRequestId] = useState<number | null>(null)
  const [snack, setSnack] = useState('')

  const meQuery = useQuery({ queryKey: masterAdminQueryKeys.me, queryFn: masterAdminApi.me })
  const permissions = meQuery.data?.permissions ?? []
  const overviewQuery = useQuery({ queryKey: masterAdminQueryKeys.overview, queryFn: masterAdminApi.overview })
  const supportQuery = useQuery({ queryKey: masterAdminQueryKeys.support, queryFn: masterAdminApi.supportList })
  // Background poll for the header status chip; the System Health page polls faster while open.
  const healthQuery = useQuery({
    queryKey: masterAdminQueryKeys.systemHealth,
    queryFn: masterAdminApi.systemHealth,
    refetchInterval: 60_000,
  })
  const overview = overviewQuery.data

  const userName = user?.full_name || [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'Administrator'
  const roleName = meQuery.data?.role_label ?? (currentPost?.role_name || currentPost?.roles_data?.roles_name || '')
  const openOrg = (o: MasterAdminOrganization) => { setSelectedOrgId(o.id); setPage('Organization Detail') }
  const openRequest = (id: number) => { setActiveRequestId(id); setPage('Support') }

  const refresh = () => { void queryClient.invalidateQueries({ queryKey: ['master-admin'] }) }
  const recheckHealth = () => { void queryClient.invalidateQueries({ queryKey: masterAdminQueryKeys.systemHealth }) }
  const refreshing = overviewQuery.isFetching || supportQuery.isFetching

  const handleLogout = async () => {
    try {
      await authApi.logout()
    } catch (error) {
      console.error('Logout failed:', error)
    }
    localStorage.clear()
    window.location.href = '/login'
  }

  const body = useMemo(() => {
    if (!overview) return null
    // Terminated orgs come back in the list so they can be found and reactivated; every other
    // page is about current customers, so it gets the overview without them (totals already exclude them).
    const customers = {
      ...overview,
      organizations: overview.organizations.filter((o) => o.status_code !== ORG_STATUS_CODE.terminated),
    }
    switch (page) {
      case 'Organizations':
        return <Organizations overview={overview} query={query} openOrg={openOrg} parent={parentFilter} setParent={setParentFilter} />
      case 'Parent Orgs':
        return (
          <ParentOrgs
            overview={overview}
            query={query}
            openParent={(label) => { setParentFilter(label); setPage('Organizations') }}
            notify={setSnack}
          />
        )
      case 'Organization Detail':
        return (
          <OrganizationDetail
            org={overview.organizations.find((o) => o.id === selectedOrgId)}
            requests={supportQuery.data?.requests ?? []}
            setPage={setPage}
            openRequest={openRequest}
            notify={setSnack}
          />
        )
      case 'Pricing Plans':
        return <PricingPlans overview={customers} notify={setSnack} />
      case 'Billing':
        return <Billing query={query} notify={setSnack} />
      case 'Invoices':
        return <InvoicesPage query={query} />
      case 'Integration Costs':
        return <IntegrationCosts query={query} />
      case 'Support':
        return <SupportInbox overview={overview} query={query} activeId={activeRequestId} setActiveId={setActiveRequestId} notify={setSnack} />
      case 'Usage & Health':
        return <Usage overview={customers} />
      case 'Reports':
        return <Reports overview={customers} />
      case 'Audit Log':
        return <AuditLogPage query={query} />
      case 'Team':
        return permissions.includes('staff_manage')
          ? <TeamPage query={query} notify={setSnack} />
          : <Dashboard overview={customers} setPage={setPage} openOrg={openOrg} />
      case 'Platform Settings':
        return <Settings overview={customers} notify={setSnack} />
      default:
        return <Dashboard overview={customers} setPage={setPage} openOrg={openOrg} />
    }
    // openOrg/openRequest only close over stable state setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, query, overview, selectedOrgId, activeRequestId, supportQuery.data, permissions, parentFilter])

  return (
    <AccessContext.Provider value={permissions}>
    <Box className="fpma">
      <Sidebar
        page={page}
        setPage={setPage}
        userName={userName}
        roleName={roleName}
        onClassic={meQuery.data?.role === 'owner' ? () => navigate('/dashboard') : undefined}
        onLogout={handleLogout}
      />
      <Box className="content">
        <Header
          query={query}
          setQuery={setQuery}
          onRefresh={refresh}
          refreshing={refreshing}
          health={healthQuery.data?.status}
          onHealthClick={() => setPage('System Health')}
        />
        <Box className="main">
          {/* System Health must still render when the overview fails (e.g. database down). */}
          {meQuery.error
            ? <Alert severity="error">{getErrorMessage(meQuery.error, "You don't have access to the master admin.")}</Alert>
            : page === 'System Health'
              ? <SystemHealth onRecheck={recheckHealth} />
              : <QueryGate isLoading={overviewQuery.isLoading} error={overviewQuery.error}>{body}</QueryGate>}
        </Box>
      </Box>
      <Snackbar open={!!snack} autoHideDuration={2600} onClose={() => setSnack('')} message={snack} />
    </Box>
    </AccessContext.Provider>
  )
}
